import React, { useState, useEffect } from 'react';
import { runAdminTests, getTestRuns, getTestRunDetail, getAdminTestHealth } from '../../services/api';
import TestRunner from './TestRunner';
import TestResults from './TestResults';
import TestHistory from './TestHistory';
import DataLoading from './DataLoading';
import RolesManagement from './RolesManagement';
import Reports from './Reports';
import PendingDeletions from './PendingDeletions';
import TestUserCleanup from './TestUserCleanup';
import VideoManager from './VideoManager';
import AffiliatePayouts from './AffiliatePayouts';
import TestProgressModal from './TestProgressModal';
import './AdminTestStyles.css';

// Mirrors PRODUCTION_MAX_TESTS_PER_RUN in admin_tests/views.py; the backend
// enforces it (400 selection_too_large), this only explains it up front.
const PRODUCTION_MAX_TESTS_PER_RUN = 60;

// The backend's error text for a refused run: 503 runner_misconfigured,
// 409 run_in_progress and 400 selection_required/selection_too_large carry
// `error`; DRF errors carry `detail`; serializer errors are a field map.
const describeRunError = (err) => {
  const data = err.response?.data;
  if (data && typeof data === 'object') {
    if (data.error) return data.error;
    if (data.detail) return data.detail;
    const flatten = (value) => (
      value && typeof value === 'object' ? Object.values(value).flatMap(flatten) : [String(value)]
    );
    const messages = flatten(data);
    if (messages.length) return 'The server refused the run: ' + messages.join(' ');
  }
  return 'Failed to run tests: ' + err.message;
};

function AdminTestDashboard() {
  // Access is the /admin-tests RequireAdmin route plus a staff-token check on
  // the backend; there's no separate password. Roles is the default tab.
  const [activeTab, setActiveTab] = useState('roles');
  const [environment, setEnvironment] = useState('development');
  const [selectedTests, setSelectedTests] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [currentRun, setCurrentRun] = useState(null);
  const [testHistory, setTestHistory] = useState([]);
  const [error, setError] = useState('');
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [currentTestRunId, setCurrentTestRunId] = useState(null);
  // From admin-tests/health/: runner_enabled, runner_mode ('development' |
  // 'production', decided by the server) and test_account_status. Null until
  // loaded, or when the check fails; the Run Tests tab then falls back to
  // the hostname.
  const [serverInfo, setServerInfo] = useState(null);

  // Load test history and runner status on mount, and drop the password the
  // old prompt stored
  useEffect(() => {
    localStorage.removeItem('adminTestsPassword');
    loadTestHistory();
    loadServerInfo();
  }, []);

  const loadTestHistory = async () => {
    try {
      const runs = await getTestRuns();
      setTestHistory(runs);
    } catch (err) {
      console.error('Failed to load test history:', err);
    }
  };

  const loadServerInfo = async () => {
    try {
      setServerInfo(await getAdminTestHealth());
    } catch (err) {
      console.error('Failed to load admin test health:', err);
    }
  };

  const isProductionServer = serverInfo?.runner_mode === 'production';

  // Available test types organized by module and component
  const testTypes = [
    // ============================================
    // SECURITY CONFIGURATION MODULE (P0 Items)
    // ============================================
    {
      id: 'security_secret_key_env',
      name: 'SECRET_KEY from Environment',
      category: 'Security Configuration',
      module: 'Security',
      component: 'Django Settings',
      description: 'Verifies SECRET_KEY is loaded from DJANGO_SECRET_KEY environment variable and not hardcoded'
    },
    {
      id: 'security_secret_key_production',
      name: 'SECRET_KEY Production Validation',
      category: 'Security Configuration',
      module: 'Security',
      component: 'Django Settings',
      description: 'Verifies production mode requires SECRET_KEY and prevents deployment without proper configuration'
    },
    {
      id: 'security_allowed_hosts',
      name: 'ALLOWED_HOSTS Configuration',
      category: 'Security Configuration',
      module: 'Security',
      component: 'Django Settings',
      description: 'Verifies ALLOWED_HOSTS does not contain wildcard "*" (host header attack protection)'
    },
    {
      id: 'security_api_url_config',
      name: 'API URL Not Hardcoded (P0-3)',
      category: 'Security Configuration',
      module: 'Security',
      component: 'Frontend API Config',
      description: 'Verifies frontend API URL reads from REACT_APP_API_URL environment variable with production validation and HTTPS enforcement'
    },
    {
      id: 'security_debug_disabled',
      name: 'DEBUG Mode Disabled in Production (P0-4)',
      category: 'Security Configuration',
      module: 'Security',
      component: 'Frontend API Config',
      description: 'Verifies DEBUG_API is environment-aware and disabled in production builds to prevent exposing tokens and sensitive data'
    },

    // ============================================
    // RATE LIMITING MODULE (P0-5)
    // ============================================
    {
      id: 'rate_limit_login_throttle',
      name: 'Login Throttle (50/hour)',
      category: 'Rate Limiting',
      module: 'Security',
      component: 'Authentication',
      description: 'Verifies login endpoint is throttled at 50 attempts/hour per IP to prevent brute force password attacks'
    },
    {
      id: 'rate_limit_signup_throttle',
      name: 'Signup Throttle (10/hour)',
      category: 'Rate Limiting',
      module: 'Security',
      component: 'Authentication',
      description: 'Verifies signup endpoint is throttled at 10 accounts/hour per IP to prevent spam account creation'
    },
    {
      id: 'rate_limit_screener_throttle',
      name: 'Screener Throttle (1000/hour)',
      category: 'Rate Limiting',
      module: 'Security',
      component: 'Screener/Watchlist',
      description: 'Verifies expensive operations (screener, watchlist) are throttled at 1000 requests/hour per user to prevent resource abuse'
    },
    {
      id: 'rate_limit_cache_backend',
      name: 'Cache Backend Configuration',
      category: 'Rate Limiting',
      module: 'Security',
      component: 'Django Settings',
      description: 'Verifies Django cache backend is configured correctly for storing rate limit counters'
    },
    {
      id: 'rate_limit_production_config',
      name: 'Production Configuration',
      category: 'Rate Limiting',
      module: 'Security',
      component: 'Django Settings',
      description: 'Verifies all throttle classes are applied to endpoints with correct rate limits (login: 50/hour, signup: 10/hour, screener: 1000/hour)'
    },

    // ============================================
    // AUTHENTICATION MODULE
    // ============================================
    {
      id: 'auth_login',
      name: 'Login',
      category: 'Authentication',
      module: 'Authentication',
      component: 'Login/Registration',
      description: 'Verifies user login endpoint with valid credentials and token generation'
    },
    {
      id: 'auth_signup',
      name: 'Signup',
      category: 'Authentication',
      module: 'Authentication',
      component: 'Login/Registration',
      description: 'Tests new user registration and automatic token creation'
    },
    {
      id: 'auth_token',
      name: 'Token Validation',
      category: 'Authentication',
      module: 'Authentication',
      component: 'Login/Registration',
      description: 'Validates that protected endpoints correctly require authentication tokens'
    },

    // ============================================
    // GOOGLE OAUTH MODULE
    // ============================================
    {
      id: 'auth_google_oauth_endpoint',
      name: 'Google OAuth Endpoint',
      category: 'Authentication > Google OAuth',
      module: 'Authentication',
      component: 'Google OAuth',
      description: 'Verifies that the Google OAuth endpoint exists and is properly configured at /api/auth/google/'
    },
    {
      id: 'auth_google_oauth_invalid_token',
      name: 'Google OAuth Invalid Token',
      category: 'Authentication > Google OAuth',
      module: 'Authentication',
      component: 'Google OAuth',
      description: 'Tests that Google OAuth correctly rejects invalid/fake JWT tokens with 400/401 response'
    },
    {
      id: 'auth_google_oauth_missing_credential',
      name: 'Google OAuth Missing Credential',
      category: 'Authentication > Google OAuth',
      module: 'Authentication',
      component: 'Google OAuth',
      description: 'Validates proper error handling when credential field is missing or malformed'
    },

    // ============================================
    // PASSWORD RESET MODULE
    // ============================================
    {
      id: 'auth_password_reset_request',
      name: 'Password Reset Request',
      category: 'Authentication > Password Reset',
      module: 'Authentication',
      component: 'Password Reset',
      description: 'Tests the password reset request endpoint at /api/auth/password-reset/request/'
    },
    {
      id: 'auth_password_reset_invalid_email',
      name: 'Password Reset Invalid Email',
      category: 'Authentication > Password Reset',
      module: 'Authentication',
      component: 'Password Reset',
      description: 'Verifies proper handling of invalid email format in password reset requests'
    },
    {
      id: 'auth_password_reset_nonexistent_user',
      name: 'Password Reset Non-existent User',
      category: 'Authentication > Password Reset',
      module: 'Authentication',
      component: 'Password Reset',
      description: 'Tests security behavior for non-existent users (should return 200 to prevent email enumeration)'
    },
    {
      id: 'auth_password_reset_validate_token',
      name: 'Password Reset Token Validation',
      category: 'Authentication > Password Reset',
      module: 'Authentication',
      component: 'Password Reset',
      description: 'Tests the token validation endpoint at /api/auth/password-reset/validate/'
    },
    {
      id: 'auth_password_reset_invalid_token',
      name: 'Password Reset Invalid Token',
      category: 'Authentication > Password Reset',
      module: 'Authentication',
      component: 'Password Reset',
      description: 'Validates that password reset correctly rejects invalid/expired tokens'
    },

    // ============================================
    // ENHANCED PASSWORD RESET TESTS
    // ============================================
    {
      id: 'auth_password_reset_email_config',
      name: 'Password Reset Email Config',
      category: 'Authentication > Enhanced Password Reset',
      module: 'Authentication',
      component: 'Password Reset',
      description: 'Verifies email backend is properly configured for password reset (SMTP settings, etc.)'
    },
    {
      id: 'auth_password_reset_url_validation',
      name: 'Password Reset URL Validation',
      category: 'Authentication > Enhanced Password Reset',
      module: 'Authentication',
      component: 'Password Reset',
      description: 'Verifies reset emails contain correct production URLs (not localhost)'
    },
    {
      id: 'auth_password_reset_full_flow',
      name: 'Password Reset Full Flow',
      category: 'Authentication > Enhanced Password Reset',
      module: 'Authentication',
      component: 'Password Reset',
      description: 'Tests complete password reset flow from request to successful login with new password'
    },
    {
      id: 'auth_password_reset_email_delivery',
      name: 'Password Reset Email Delivery',
      category: 'Authentication > Enhanced Password Reset',
      module: 'Authentication',
      component: 'Password Reset',
      description: 'Verifies emails are actually sent (not just API returns 200)'
    },

    // ============================================
    // ENHANCED GOOGLE OAUTH TESTS
    // ============================================
    {
      id: 'auth_google_oauth_client_id',
      name: 'Google OAuth Client ID Check',
      category: 'Authentication > Enhanced Google OAuth',
      module: 'Authentication',
      component: 'Google OAuth',
      description: 'Verifies GOOGLE_CLIENT_ID environment variable is configured'
    },
    {
      id: 'auth_google_oauth_production_ready',
      name: 'Google OAuth Production Ready',
      category: 'Authentication > Enhanced Google OAuth',
      module: 'Authentication',
      component: 'Google OAuth',
      description: 'Verifies Google OAuth is configured for production domain (not localhost)'
    },

    // ============================================
    // PRODUCTION EMAIL CONFIGURATION TESTS
    // ============================================
    {
      id: 'prod_email_configuration',
      name: 'Email Configuration Check',
      category: 'Production > Email Configuration',
      module: 'Production',
      component: 'Email Config',
      description: 'Verifies EMAIL_HOST, EMAIL_HOST_USER, EMAIL_HOST_PASSWORD are configured in production'
    },
    {
      id: 'prod_frontend_url_configured',
      name: 'Frontend URL Check',
      category: 'Production > Email Configuration',
      module: 'Production',
      component: 'URL Config',
      description: 'Verifies FRONTEND_URL is set to production domain (not localhost)'
    },
    {
      id: 'prod_google_oauth_configured',
      name: 'Google OAuth Config Check',
      category: 'Production > Email Configuration',
      module: 'Production',
      component: 'OAuth Config',
      description: 'Verifies Google OAuth client ID and settings are configured for production'
    },
    {
      id: 'prod_email_delivery_test',
      name: 'Email Delivery Test',
      category: 'Production > Email Configuration',
      module: 'Production',
      component: 'Email Delivery',
      description: 'Tests actual email sending capability (not just configuration)'
    },

    // ============================================
    // WATCHLIST MODULE - GROUPS COMPONENT
    // ============================================
    {
      id: 'group_create',
      name: 'Create Group',
      category: 'Watchlist > Groups',
      module: 'Watchlist',
      component: 'Watchlist Groups',
      description: 'Tests basic watchlist group creation with unique name'
    },
    {
      id: 'group_update',
      name: 'Update/Rename Group',
      category: 'Watchlist > Groups',
      module: 'Watchlist',
      component: 'Watchlist Groups',
      description: 'Validates renaming and updating group properties'
    },
    {
      id: 'group_delete',
      name: 'Delete Empty Group',
      category: 'Watchlist > Groups',
      module: 'Watchlist',
      component: 'Watchlist Groups',
      description: 'Tests deletion of empty watchlist groups'
    },
    {
      id: 'group_delete_with_contracts',
      name: 'Delete Group with Contracts',
      category: 'Watchlist > Groups',
      module: 'Watchlist',
      component: 'Watchlist Groups',
      description: 'Verifies group deletion with associated contracts (cascade behavior)'
    },
    {
      id: 'group_list',
      name: 'List All Groups',
      category: 'Watchlist > Groups',
      module: 'Watchlist',
      component: 'Watchlist Groups',
      description: 'Tests retrieval of all watchlist groups for authenticated user'
    },
    {
      id: 'group_permissions',
      name: 'Group User Isolation',
      category: 'Watchlist > Groups',
      module: 'Watchlist',
      component: 'Watchlist Groups',
      description: 'Ensures users can only access their own groups (security test)'
    },
    {
      id: 'group_duplicate_names',
      name: 'Duplicate Group Names',
      category: 'Watchlist > Groups',
      module: 'Watchlist',
      component: 'Watchlist Groups',
      description: 'Verifies system allows duplicate group names for same user'
    },

    // ============================================
    // WATCHLIST MODULE - TABLE COMPONENT
    // ============================================
    {
      id: 'contract_create',
      name: 'Create Contract',
      category: 'Watchlist > Table',
      module: 'Watchlist',
      component: 'Watchlist Table',
      description: 'Tests creation of saved contract with valid options data'
    },
    {
      id: 'contract_delete',
      name: 'Delete Contract',
      category: 'Watchlist > Table',
      module: 'Watchlist',
      component: 'Watchlist Table',
      description: 'Validates deletion of individual saved contracts'
    },
    {
      id: 'contract_list',
      name: 'List All Contracts',
      category: 'Watchlist > Table',
      module: 'Watchlist',
      component: 'Watchlist Table',
      description: 'Tests retrieval of all saved contracts for authenticated user'
    },
    {
      id: 'contract_validation',
      name: 'Contract Validation',
      category: 'Watchlist > Table',
      module: 'Watchlist',
      component: 'Watchlist Table',
      description: 'Verifies input validation for invalid strikes, expirations, and tickers'
    },
    {
      id: 'contract_duplicate',
      name: 'Duplicate Contracts',
      category: 'Watchlist > Table',
      module: 'Watchlist',
      component: 'Watchlist Table',
      description: 'Tests behavior when saving duplicate contracts (same parameters)'
    },
    {
      id: 'contract_minimal',
      name: 'Minimal Fields',
      category: 'Watchlist > Table',
      module: 'Watchlist',
      component: 'Watchlist Table',
      description: 'Validates contract creation with only required fields'
    },
    {
      id: 'contract_full',
      name: 'All Fields',
      category: 'Watchlist > Table',
      module: 'Watchlist',
      component: 'Watchlist Table',
      description: 'Tests contract creation with all optional fields populated'
    },
    {
      id: 'contract_batch',
      name: 'Batch Creation',
      category: 'Watchlist > Table',
      module: 'Watchlist',
      component: 'Watchlist Table',
      description: 'Verifies ability to create multiple contracts in sequence'
    },
    {
      id: 'contract_permissions',
      name: 'Contract User Isolation',
      category: 'Watchlist > Table',
      module: 'Watchlist',
      component: 'Watchlist Table',
      description: 'Ensures users can only access their own contracts (security test)'
    },
    {
      id: 'ops_reset_days',
      name: 'Reset Days to Gain',
      category: 'Watchlist > Table',
      module: 'Watchlist',
      component: 'Watchlist Table',
      description: 'Tests resetting days-to-gain parameter for contract analysis'
    },
    {
      id: 'watchlist_refresh',
      name: 'Refresh Single Contract',
      category: 'Watchlist > Table',
      module: 'Watchlist',
      component: 'Watchlist Table',
      description: 'Verifies contract data refresh with latest market prices from EODHD API'
    },
    {
      id: 'ops_bulk_refresh',
      name: 'Bulk Refresh',
      category: 'Watchlist > Table',
      module: 'Watchlist',
      component: 'Watchlist Table',
      description: 'Validates refreshing market data for multiple contracts at once'
    },
    {
      id: 'watchlist_days_to_target',
      name: 'Save with Days to Target',
      category: 'Watchlist > Params Form',
      module: 'Watchlist',
      component: 'Watchlist Params Form',
      description: 'Tests saving contract with days_to_target from WatchlistParamsForm'
    },
    {
      id: 'watchlist_avg_cost_initial',
      name: 'Average Cost as Initial Premium',
      category: 'Watchlist > Params Form',
      module: 'Watchlist',
      component: 'Watchlist Params Form',
      description: 'Verifies average_cost_per_contract sets initial_premium when provided'
    },
    {
      id: 'watchlist_update_avg_cost',
      name: 'Update Average Cost Updates Initial',
      category: 'Watchlist > Contract Settings',
      module: 'Watchlist',
      component: 'Contract Settings',
      description: 'Tests that updating average_cost_per_contract also updates initial_premium'
    },

    // ============================================
    // WATCHLIST MODULE - PARAMS FORM COMPONENT
    // ============================================
    {
      id: 'watchlist_simulate',
      name: 'Run Simulation',
      category: 'Watchlist > Params Form',
      module: 'Watchlist',
      component: 'Watchlist Params Form',
      description: 'Validates simulation functionality with price scenarios and P&L calculations'
    },
    {
      id: 'watchlist_contract',
      name: 'Save Contract from Form',
      category: 'Watchlist > Params Form',
      module: 'Watchlist',
      component: 'Watchlist Params Form',
      description: 'Tests saving an options contract from params form with all required fields'
    },
    {
      id: 'ops_bulk_simulation',
      name: 'Bulk Simulation',
      category: 'Watchlist > Params Form',
      module: 'Watchlist',
      component: 'Watchlist Params Form',
      description: 'Tests running simulations on multiple contracts in batch'
    },

    // ============================================
    // SCREENER TO WATCHLIST FLOW
    // ============================================
    {
      id: 'screener_watchlist_save',
      name: 'Save Contract from Screener',
      category: 'Screener > Watchlist',
      module: 'Screener',
      component: 'ScreenerResultsTable',
      description: 'Verifies contract is saved with correct fields when saving from screener results'
    },
    {
      id: 'screener_watchlist_fields',
      name: 'Verify Auto-Calculated Fields',
      category: 'Screener > Watchlist',
      module: 'Screener',
      component: 'ScreenerResultsTable',
      description: 'Tests that auto-calculated fields (initial_premium, current_premium, underlying_price_at_add, initial_equity) are populated'
    },
    {
      id: 'screener_watchlist_assign',
      name: 'Assign Screener Contract to Group',
      category: 'Screener > Watchlist',
      module: 'Screener',
      component: 'ScreenerResultsTable',
      description: 'Verifies screener-saved contract can be assigned to a watchlist group'
    },
    {
      id: 'screener_watchlist_flow',
      name: 'End-to-End Screener to Watchlist',
      category: 'Screener > Watchlist',
      module: 'Screener',
      component: 'ScreenerResultsTable',
      description: 'Tests complete flow: save contract from screener, create group, assign to group, verify data integrity'
    },

    // ============================================
    // SCREENER MODULE - FAVORITES
    // ============================================
    {
      id: 'screener_favorites_row_keys',
      name: 'Row Key Fields Present',
      category: 'Screener > Favorites',
      module: 'Screener',
      component: 'ScreenerResultsTable',
      description: 'Verifies screener results contain Ticker, Option Type, Strike, Expiration fields needed for favorites row keys'
    },
    {
      id: 'screener_favorites_unique_keys',
      name: 'Unique Row Keys',
      category: 'Screener > Favorites',
      module: 'Screener',
      component: 'ScreenerResultsTable',
      description: 'Verifies all screener result row keys are unique (no duplicates that would break Set-based favorites tracking)'
    },
    {
      id: 'screener_favorites_multi_ticker',
      name: 'Multi-Ticker Unique Keys',
      category: 'Screener > Favorites',
      module: 'Screener',
      component: 'ScreenerResultsTable',
      description: 'Verifies row keys remain unique when screening multiple tickers simultaneously'
    },
    {
      id: 'screener_favorites_sortable_fields',
      name: 'Sortable Fields Present',
      category: 'Screener > Favorites',
      module: 'Screener',
      component: 'ScreenerResultsTable',
      description: 'Verifies fields needed for favorites sorting and filtering (Premium % Gain, Current Premium, etc.) are present'
    },
    {
      id: 'screener_favorites_save_workflow',
      name: 'Favorites Save to Watchlist Flow',
      category: 'Screener > Favorites',
      module: 'Screener',
      component: 'ScreenerResultsTable',
      description: 'Tests the favorites-to-watchlist workflow: run screener, pick a result, save to watchlist, verify data integrity'
    },

    // ============================================
    // SCREENER MODULE - ELASTICITY HEATMAP
    // ============================================
    {
      id: 'heatmap_essential_fields',
      name: 'Essential Fields Present',
      category: 'Screener > Elasticity Heatmap',
      module: 'Screener',
      component: 'ElasticityHeatmapView',
      description: 'Verifies screener results contain all fields needed by heatmap essential columns (Ticker, Strike, Premium, IV, Strike Position, etc.)'
    },
    {
      id: 'heatmap_iv_numeric',
      name: 'IV Values Numeric',
      category: 'Screener > Elasticity Heatmap',
      module: 'Screener',
      component: 'ElasticityHeatmapView',
      description: 'Verifies Implied Volatility values are numeric and positive, required for client-side Black-Scholes computation'
    },
    {
      id: 'heatmap_strike_position',
      name: 'Strike Position for Sort',
      category: 'Screener > Elasticity Heatmap',
      module: 'Screener',
      component: 'ElasticityHeatmapView',
      description: 'Verifies Strike Position field exists and is numeric for default sort order (Ticker → Expiration → Strike Position ITM to OTM)'
    },
    {
      id: 'heatmap_bs_input_fields',
      name: 'Black-Scholes Input Fields',
      category: 'Screener > Elasticity Heatmap',
      module: 'Screener',
      component: 'ElasticityHeatmapView',
      description: 'Verifies all fields needed for client-side Black-Scholes computation (Underlying Price, Strike, DTE, Premium, IV, Option Type, Position Type)'
    },
    {
      id: 'heatmap_multi_ticker',
      name: 'Multi-Ticker Heatmap Data',
      category: 'Screener > Elasticity Heatmap',
      module: 'Screener',
      component: 'ElasticityHeatmapView',
      description: 'Verifies multi-ticker screener results have complete heatmap-ready data across all tickers'
    },
    {
      id: 'heatmap_save_to_watchlist',
      name: 'Heatmap Save to Watchlist Flow',
      category: 'Screener > Elasticity Heatmap',
      module: 'Screener',
      component: 'ElasticityHeatmapView',
      description: 'Tests the heatmap-to-watchlist save workflow: run screener, pick a heatmap result, save as watchlist contract'
    },
    {
      id: 'heatmap_buy_call',
      name: 'Buy Call Fields',
      category: 'Screener > Elasticity Heatmap',
      module: 'Screener',
      component: 'ElasticityHeatmapView',
      description: 'Verifies buy call results have correct Option Type, Position Type, and valid BS input fields for heatmap computation'
    },
    {
      id: 'heatmap_buy_put',
      name: 'Buy Put Fields',
      category: 'Screener > Elasticity Heatmap',
      module: 'Screener',
      component: 'ElasticityHeatmapView',
      description: 'Verifies buy put results have correct Option Type, Position Type, and valid BS input fields for heatmap computation'
    },
    {
      id: 'heatmap_sell_call',
      name: 'Sell Call Fields',
      category: 'Screener > Elasticity Heatmap',
      module: 'Screener',
      component: 'ElasticityHeatmapView',
      description: 'Verifies sell call results have correct Option Type, Position Type, and inverted gain direction fields for heatmap'
    },
    {
      id: 'heatmap_sell_put',
      name: 'Sell Put Fields',
      category: 'Screener > Elasticity Heatmap',
      module: 'Screener',
      component: 'ElasticityHeatmapView',
      description: 'Verifies sell put results have correct Option Type, Position Type, and inverted gain direction fields for heatmap'
    },
    {
      id: 'heatmap_breakeven_buy_call',
      name: 'Breakeven Buy Call',
      category: 'Screener > Elasticity Heatmap',
      module: 'Screener',
      component: 'ElasticityHeatmapView',
      description: 'Validates buy call breakeven via B-S binary search: breakeven > strike, P&L ≈ 0 at breakeven price'
    },
    {
      id: 'heatmap_breakeven_buy_put',
      name: 'Breakeven Buy Put',
      category: 'Screener > Elasticity Heatmap',
      module: 'Screener',
      component: 'ElasticityHeatmapView',
      description: 'Validates buy put breakeven via B-S binary search: breakeven < strike, P&L ≈ 0 at breakeven price'
    },
    {
      id: 'heatmap_breakeven_sell_call',
      name: 'Breakeven Sell Call',
      category: 'Screener > Elasticity Heatmap',
      module: 'Screener',
      component: 'ElasticityHeatmapView',
      description: 'Validates sell call breakeven via B-S binary search: breakeven > strike, inverted P&L ≈ 0'
    },
    {
      id: 'heatmap_breakeven_sell_put',
      name: 'Breakeven Sell Put',
      category: 'Screener > Elasticity Heatmap',
      module: 'Screener',
      component: 'ElasticityHeatmapView',
      description: 'Validates sell put breakeven via B-S binary search: breakeven < strike, inverted P&L ≈ 0'
    },

    // ============================================
    // SCREENER MODULE - CONTRACT POPUP
    // ============================================
    {
      id: 'screener_popup_charts_data',
      name: 'Charts Tab - Options Data',
      category: 'Screener > Contract Popup',
      module: 'Screener',
      component: 'OptionsPremiumChartModal',
      description: 'Verifies the popup Charts tab options-historical endpoint resolves for a contract discovered by a screener run (not just hardcoded watchlist contracts)'
    },
    {
      id: 'screener_popup_underlying_chart',
      name: 'Charts Tab - Underlying Data',
      category: 'Screener > Contract Popup',
      module: 'Screener',
      component: 'OptionsPremiumChartModal',
      description: 'Verifies the popup Charts tab underlying chart-data endpoint returns OHLCV data for the screener result ticker'
    },
    {
      id: 'screener_popup_full_flow',
      name: 'All Tabs Resolve',
      category: 'Screener > Contract Popup',
      module: 'Screener',
      component: 'OptionsPremiumChartModal',
      description: 'End-to-end: one screener result row resolves all 3 popup tabs - Charts (chart endpoint), Returns (PnL simulator fields), Details (heatmap + Greeks fields)'
    },
    {
      id: 'screener_popup_put_contract',
      name: 'Put Contract Flow',
      category: 'Screener > Contract Popup',
      module: 'Screener',
      component: 'OptionsPremiumChartModal',
      description: 'Repeats the all-tabs-resolve check for a put screener result (puts route differently through Black-Scholes and the popup tabs)'
    },
    {
      id: 'screener_popup_illiquid_graceful',
      name: 'Illiquid Contract Graceful',
      category: 'Screener > Contract Popup',
      module: 'Screener',
      component: 'OptionsPremiumChartModal',
      description: 'Verifies the Charts tab degrades gracefully on an illiquid deep-OTM screener contract: options-historical returns HTTP 200 even with empty trade history'
    },

    // ============================================
    // WATCHLIST MODULE - GROUP ASSOCIATIONS
    // ============================================
    {
      id: 'watchlist_create',
      name: 'Create Group & Assign',
      category: 'Watchlist > Group Associations',
      module: 'Watchlist',
      component: 'Group Associations',
      description: 'Tests creation of a new watchlist group and assigning contracts'
    },
    {
      id: 'assoc_assign_single',
      name: 'Assign Single Contract',
      category: 'Watchlist > Group Associations',
      module: 'Watchlist',
      component: 'Group Associations',
      description: 'Tests assigning a single contract to a watchlist group'
    },
    {
      id: 'assoc_assign_multiple',
      name: 'Assign Multiple Contracts',
      category: 'Watchlist > Group Associations',
      module: 'Watchlist',
      component: 'Group Associations',
      description: 'Validates assigning multiple contracts to one group simultaneously'
    },
    {
      id: 'assoc_remove',
      name: 'Remove from Group',
      category: 'Watchlist > Group Associations',
      module: 'Watchlist',
      component: 'Group Associations',
      description: 'Tests removing contract assignments from groups (unlink)'
    },
    {
      id: 'assoc_reassign',
      name: 'Reassign to Different Group',
      category: 'Watchlist > Group Associations',
      module: 'Watchlist',
      component: 'Group Associations',
      description: 'Verifies moving contracts from one group to another'
    },
    {
      id: 'assoc_replace_mode',
      name: 'Replace Mode Assignment',
      category: 'Watchlist > Group Associations',
      module: 'Watchlist',
      component: 'Group Associations',
      description: 'Tests replace mode that clears existing associations before assigning'
    },
    {
      id: 'assoc_multiple_groups',
      name: 'Multiple Groups per Contract',
      category: 'Watchlist > Group Associations',
      module: 'Watchlist',
      component: 'Group Associations',
      description: 'Validates assigning same contract to multiple groups (ManyToMany)'
    },
    {
      id: 'assoc_save_persist',
      name: 'Save & Persist Contracts',
      category: 'Watchlist > Group Associations',
      module: 'Watchlist',
      component: 'Group Associations',
      description: 'Tests that contracts persist after saving and assigning to a group (verifies form state preservation fix)'
    },

    // ============================================
    // WATCHLIST MODULE - GROUP OPERATIONS
    // ============================================
    {
      id: 'ops_group_refresh',
      name: 'Refresh Group Contracts',
      category: 'Watchlist > Group Operations',
      module: 'Watchlist',
      component: 'Group Operations',
      description: 'Verifies refreshing all contracts within a specific group'
    },
    {
      id: 'ops_simulation_empty',
      name: 'Simulate Empty Group',
      category: 'Watchlist > Group Operations',
      module: 'Watchlist',
      component: 'Group Operations',
      description: 'Tests simulation behavior when run on an empty watchlist group'
    },

    // ============================================
    // SELL-SIDE MODULE - CONTRACT & SIMULATION
    // ============================================
    {
      id: 'sell_contract_put',
      name: 'Create Sell Put Contract',
      category: 'Sell Side > Contracts',
      module: 'Watchlist',
      component: 'Sell Side',
      description: 'Tests creating a sell put contract with position_type=sell'
    },
    {
      id: 'sell_contract_call',
      name: 'Create Sell Call Contract',
      category: 'Sell Side > Contracts',
      module: 'Watchlist',
      component: 'Sell Side',
      description: 'Tests creating a sell call contract with position_type=sell'
    },
    {
      id: 'sell_default_buy',
      name: 'Default Position Type is Buy',
      category: 'Sell Side > Contracts',
      module: 'Watchlist',
      component: 'Sell Side',
      description: 'Verifies position_type defaults to buy when not specified'
    },
    {
      id: 'sell_position_persist',
      name: 'Position Type Persists',
      category: 'Sell Side > Contracts',
      module: 'Watchlist',
      component: 'Sell Side',
      description: 'Verifies sell position_type is retained after save and retrieve'
    },
    {
      id: 'sell_simulate_put',
      name: 'Simulate Sell Put',
      category: 'Sell Side > Simulation',
      module: 'Watchlist',
      component: 'Sell Side',
      description: 'Runs watchlist simulation on a sell put and verifies sell-specific fields'
    },
    {
      id: 'sell_simulate_call',
      name: 'Simulate Sell Call',
      category: 'Sell Side > Simulation',
      module: 'Watchlist',
      component: 'Sell Side',
      description: 'Runs watchlist simulation on a sell call and verifies sell-specific fields'
    },
    {
      id: 'sell_fields_present',
      name: 'Sell Fields Present in Results',
      category: 'Sell Side > Simulation',
      module: 'Watchlist',
      component: 'Sell Side',
      description: 'Verifies Collateral Required, Return on Collateral %, Max Profit, Max Loss, Simulated P&L columns are present'
    },
    {
      id: 'sell_max_loss_call',
      name: 'Max Loss Unlimited for Calls',
      category: 'Sell Side > Simulation',
      module: 'Watchlist',
      component: 'Sell Side',
      description: 'Verifies Max Loss is Unlimited for sell call positions'
    },
    {
      id: 'sell_collateral_put',
      name: 'Put Collateral Calculation',
      category: 'Sell Side > Simulation',
      module: 'Watchlist',
      component: 'Sell Side',
      description: 'Verifies cash-secured put collateral equals strike x 100'
    },
    {
      id: 'sell_collateral_call',
      name: 'Call Collateral Calculation',
      category: 'Sell Side > Simulation',
      module: 'Watchlist',
      component: 'Sell Side',
      description: 'Verifies naked call collateral equals (stock x 0.20 + premium) x 100'
    },
    {
      id: 'sell_group_simulate',
      name: 'Group Simulation with Sells',
      category: 'Sell Side > Group Ops',
      module: 'Watchlist',
      component: 'Sell Side',
      description: 'Creates group with sell contracts, runs simulation, verifies sell fields'
    },
    {
      id: 'sell_screener',
      name: 'Screener Sell Mode',
      category: 'Sell Side > Screener',
      module: 'Screener',
      component: 'Sell Side',
      description: 'Runs screener with position_type=sell and verifies sell-specific fields in results'
    },

    // ============================================
    // WATCHLIST MODULE - CHARTS (Contract Card Charts Tab)
    // ============================================
    {
      id: 'watchlist_chart_options_fetch',
      name: 'Options Chart Fetch',
      category: 'Watchlist > Charts',
      module: 'Watchlist',
      component: 'Contract Card Charts',
      description: 'Tests fetching options premium historical data for chart display'
    },
    {
      id: 'watchlist_chart_underlying_fetch',
      name: 'Underlying Chart Fetch',
      category: 'Watchlist > Charts',
      module: 'Watchlist',
      component: 'Contract Card Charts',
      description: 'Tests fetching underlying stock price data for chart display'
    },
    {
      id: 'watchlist_chart_both_endpoints',
      name: 'Both Chart Endpoints',
      category: 'Watchlist > Charts',
      module: 'Watchlist',
      component: 'Contract Card Charts',
      description: 'Verifies both chart endpoints work for the same contract (supports Line em up)'
    },
    {
      id: 'watchlist_chart_date_range_1m',
      name: '1-Month Date Range',
      category: 'Watchlist > Charts',
      module: 'Watchlist',
      component: 'Contract Card Charts',
      description: 'Tests 1-month date range works on both chart endpoints'
    },
    {
      id: 'watchlist_chart_date_range_3m',
      name: '3-Month Date Range',
      category: 'Watchlist > Charts',
      module: 'Watchlist',
      component: 'Contract Card Charts',
      description: 'Tests 3-month date range works on both chart endpoints'
    },
    {
      id: 'watchlist_chart_date_format',
      name: 'Date Format Compatibility',
      category: 'Watchlist > Charts',
      module: 'Watchlist',
      component: 'Contract Card Charts',
      description: 'Verifies both endpoints return matching date formats for crosshair sync'
    },
    {
      id: 'watchlist_chart_data_structure',
      name: 'Data Structure Validation',
      category: 'Watchlist > Charts',
      module: 'Watchlist',
      component: 'Contract Card Charts',
      description: 'Validates OHLCV-like data structure for chart rendering'
    },
    {
      id: 'watchlist_chart_contract_params',
      name: 'Contract-Based Parameters',
      category: 'Watchlist > Charts',
      module: 'Watchlist',
      component: 'Contract Card Charts',
      description: 'Tests chart APIs work with parameters from saved contracts'
    },

    // ============================================
    // WATCHLIST MODULE - SIMULATED RETURNS (PnL Simulator)
    // ============================================
    {
      id: 'sim_returns_screener_fields',
      name: 'Screener Fields Complete',
      category: 'Watchlist > Simulated Returns',
      module: 'Watchlist',
      component: 'PnL Simulator',
      description: 'Validates screener results contain all fields needed by PnL simulator (Ticker, Strike, Premium, IV, Greeks, DTE)'
    },
    {
      id: 'sim_returns_watchlist_fields',
      name: 'Watchlist Fields Complete',
      category: 'Watchlist > Simulated Returns',
      module: 'Watchlist',
      component: 'PnL Simulator',
      description: 'Validates watchlist simulation returns all fields needed by PnL simulator (Current Underlying, etc.)'
    },
    {
      id: 'sim_returns_bs_call_known',
      name: 'B-S Call Known Values',
      category: 'Watchlist > Simulated Returns',
      module: 'Watchlist',
      component: 'PnL Simulator',
      description: 'Validates Black-Scholes call pricing against known analytical values (ATM, deep ITM, deep OTM)'
    },
    {
      id: 'sim_returns_bs_put_known',
      name: 'B-S Put Known Values',
      category: 'Watchlist > Simulated Returns',
      module: 'Watchlist',
      component: 'PnL Simulator',
      description: 'Validates Black-Scholes put pricing and put-call parity (C - P = S - Ke^(-rT))'
    },
    {
      id: 'sim_returns_bs_edge_cases',
      name: 'B-S Edge Cases',
      category: 'Watchlist > Simulated Returns',
      module: 'Watchlist',
      component: 'PnL Simulator',
      description: 'Tests B-S with edge cases: expired (T=0), zero vol, T=0.0001 (PnL sim minimum), extreme S/K ratios'
    },
    {
      id: 'sim_returns_iv_present',
      name: 'IV Data Available',
      category: 'Watchlist > Simulated Returns',
      module: 'Watchlist',
      component: 'PnL Simulator',
      description: 'Verifies Implied Volatility is a valid positive number (warns if default 30% fallback)'
    },
    {
      id: 'sim_returns_greeks_present',
      name: 'Greeks Data Available',
      category: 'Watchlist > Simulated Returns',
      module: 'Watchlist',
      component: 'PnL Simulator',
      description: 'Verifies Delta [-1,1] and Theta are real numbers displayed in PnL simulator metrics row'
    },
    {
      id: 'sim_returns_position_types',
      name: 'Buy/Sell Position Data',
      category: 'Watchlist > Simulated Returns',
      module: 'Watchlist',
      component: 'PnL Simulator',
      description: 'Verifies Position Type field and sell-specific fields (Collateral, Max Loss) for inverted P&L logic'
    },
    {
      id: 'sim_returns_breakeven_buy_call',
      name: 'Breakeven Buy Call',
      category: 'Watchlist > Simulated Returns',
      module: 'Watchlist',
      component: 'PnL Simulator',
      description: 'Validates buy call breakeven with known B-S values: breakeven > strike, P&L = 0 at breakeven'
    },
    {
      id: 'sim_returns_breakeven_buy_put',
      name: 'Breakeven Buy Put',
      category: 'Watchlist > Simulated Returns',
      module: 'Watchlist',
      component: 'PnL Simulator',
      description: 'Validates buy put breakeven with known B-S values: breakeven < strike, P&L = 0 at breakeven'
    },
    {
      id: 'sim_returns_breakeven_sell_call',
      name: 'Breakeven Sell Call',
      category: 'Watchlist > Simulated Returns',
      module: 'Watchlist',
      component: 'PnL Simulator',
      description: 'Validates sell call breakeven with known B-S values: inverted P&L direction, breakeven > strike'
    },
    {
      id: 'sim_returns_breakeven_sell_put',
      name: 'Breakeven Sell Put',
      category: 'Watchlist > Simulated Returns',
      module: 'Watchlist',
      component: 'PnL Simulator',
      description: 'Validates sell put breakeven with known B-S values: inverted P&L direction, breakeven < strike'
    },
    {
      id: 'sim_returns_breakeven_consistency',
      name: 'Breakeven Heatmap/Modal Consistency',
      category: 'Watchlist > Simulated Returns',
      module: 'Watchlist',
      component: 'PnL Simulator',
      description: 'Verifies heatmap tooltip and modal use identical breakeven logic across all 4 position/type combos'
    },

    // ============================================
    // WATCHLIST MODULE - TARGET MANAGEMENT
    // ============================================
    {
      id: 'target_set_positive',
      name: 'Set Positive Target',
      category: 'Watchlist > Target Management',
      module: 'Watchlist',
      component: 'Target Tracking',
      description: 'Tests setting a positive (gain) target percentage (+20%)'
    },
    {
      id: 'target_set_negative',
      name: 'Set Negative Target',
      category: 'Watchlist > Target Management',
      module: 'Watchlist',
      component: 'Target Tracking',
      description: 'Tests setting a negative (loss) target percentage (-8.5%)'
    },
    {
      id: 'target_update',
      name: 'Update Existing Target',
      category: 'Watchlist > Target Management',
      module: 'Watchlist',
      component: 'Target Tracking',
      description: 'Tests updating an existing target from one value to another'
    },
    {
      id: 'target_remove',
      name: 'Remove Target',
      category: 'Watchlist > Target Management',
      module: 'Watchlist',
      component: 'Target Tracking',
      description: 'Tests removing a target by setting it to null'
    },
    {
      id: 'target_with_days',
      name: 'Set Target with Days',
      category: 'Watchlist > Target Management',
      module: 'Watchlist',
      component: 'Target Tracking',
      description: 'Tests setting target percentage with days_to_gain simultaneously'
    },
    {
      id: 'target_update_contracts',
      name: 'Update Contracts & Cost',
      category: 'Watchlist > Target Management',
      module: 'Watchlist',
      component: 'Target Tracking',
      description: 'Tests updating number of contracts and cost along with target'
    },
    {
      id: 'target_validation',
      name: 'Input Validation',
      category: 'Watchlist > Target Management',
      module: 'Watchlist',
      component: 'Target Tracking',
      description: 'Tests validation of invalid target values (negative days, zero contracts)'
    },
    {
      id: 'target_serializer',
      name: 'Serializer Includes Target',
      category: 'Watchlist > Target Management',
      module: 'Watchlist',
      component: 'Target Tracking',
      description: 'Tests that target_percent_change appears in contract list/detail responses'
    },

    // ============================================
    // SCREENER MODULE - BASIC MODE
    // ============================================
    {
      id: 'screener_aggressive_itm',
      name: 'Aggressive ITM',
      category: 'Screener > Basic Mode',
      module: 'Screener',
      component: 'Basic Mode Grid',
      description: 'Tests Quick ITM strategy (3mo, 80% strike) with 35% days-to-gain calculation'
    },
    {
      id: 'screener_aggressive_atm',
      name: 'Aggressive ATM',
      category: 'Screener > Basic Mode',
      module: 'Screener',
      component: 'Basic Mode Grid',
      description: 'Tests Quick ATM strategy (3mo, 100% strike) at-the-money options'
    },
    {
      id: 'screener_aggressive_otm',
      name: 'Aggressive OTM',
      category: 'Screener > Basic Mode',
      module: 'Screener',
      component: 'Basic Mode Grid',
      description: 'Tests Quick OTM strategy (3mo, 120% strike) out-of-the-money options'
    },
    {
      id: 'screener_aggressive_deep_otm',
      name: 'Aggressive Deep OTM',
      category: 'Screener > Basic Mode',
      module: 'Screener',
      component: 'Basic Mode Grid',
      description: 'Tests Quick Lottery strategy (3mo, 150% strike) deep out-of-the-money'
    },
    {
      id: 'screener_moderate_itm',
      name: 'Moderate ITM',
      category: 'Screener > Basic Mode',
      module: 'Screener',
      component: 'Basic Mode Grid',
      description: 'Tests Balanced ITM strategy (9mo, 80% strike) medium-term options'
    },
    {
      id: 'screener_moderate_atm',
      name: 'Moderate ATM',
      category: 'Screener > Basic Mode',
      module: 'Screener',
      component: 'Basic Mode Grid',
      description: 'Tests Balanced ATM strategy (9mo, 100% strike) medium-term ATM'
    },
    {
      id: 'screener_moderate_otm',
      name: 'Moderate OTM',
      category: 'Screener > Basic Mode',
      module: 'Screener',
      component: 'Basic Mode Grid',
      description: 'Tests Balanced OTM strategy (9mo, 120% strike) medium-term OTM'
    },
    {
      id: 'screener_moderate_deep_otm',
      name: 'Moderate Deep OTM',
      category: 'Screener > Basic Mode',
      module: 'Screener',
      component: 'Basic Mode Grid',
      description: 'Tests Balanced Speculation strategy (9mo, 150% strike)'
    },
    {
      id: 'screener_conservative_itm',
      name: 'Conservative ITM (LEAPS)',
      category: 'Screener > Basic Mode',
      module: 'Screener',
      component: 'Basic Mode Grid',
      description: 'Tests LEAPS ITM strategy (14+mo, 80% strike) with extended expiration search'
    },
    {
      id: 'screener_conservative_atm',
      name: 'Conservative ATM (LEAPS)',
      category: 'Screener > Basic Mode',
      module: 'Screener',
      component: 'Basic Mode Grid',
      description: 'Tests LEAPS ATM strategy (14+mo, 100% strike) long-dated at-the-money'
    },
    {
      id: 'screener_conservative_otm',
      name: 'Conservative OTM (LEAPS)',
      category: 'Screener > Basic Mode',
      module: 'Screener',
      component: 'Basic Mode Grid',
      description: 'Tests LEAPS OTM strategy (14+mo, 120% strike) long-dated out-of-money'
    },
    {
      id: 'screener_conservative_deep_otm',
      name: 'Conservative Deep OTM (LEAPS)',
      category: 'Screener > Basic Mode',
      module: 'Screener',
      component: 'Basic Mode Grid',
      description: 'Tests LEAPS Speculation strategy (14+mo, 150% strike)'
    },

    // ============================================
    // SCREENER MODULE - DIRECTION & MOVEMENT
    // ============================================
    {
      id: 'screener_direction_bullish',
      name: 'Bullish Direction',
      category: 'Screener > Direction & Movement',
      module: 'Screener',
      component: 'Direction Toggle',
      description: 'Validates bullish direction generates call options only'
    },
    {
      id: 'screener_direction_bearish',
      name: 'Bearish Direction',
      category: 'Screener > Direction & Movement',
      module: 'Screener',
      component: 'Direction Toggle',
      description: 'Validates bearish direction generates put options only'
    },
    {
      id: 'screener_movement_10',
      name: '10% Movement',
      category: 'Screener > Direction & Movement',
      module: 'Screener',
      component: 'Movement Selector',
      description: 'Tests ±10% underlying stock movement scenario'
    },
    {
      id: 'screener_movement_25',
      name: '25% Movement',
      category: 'Screener > Direction & Movement',
      module: 'Screener',
      component: 'Movement Selector',
      description: 'Tests ±25% underlying stock movement scenario'
    },
    {
      id: 'screener_movement_50',
      name: '50% Movement',
      category: 'Screener > Direction & Movement',
      module: 'Screener',
      component: 'Movement Selector',
      description: 'Tests +50% underlying stock movement scenario'
    },
    {
      id: 'screener_movement_neg_10',
      name: '-10% Movement',
      category: 'Screener > Direction & Movement',
      module: 'Screener',
      component: 'Movement Selector',
      description: 'Tests -10% underlying stock movement scenario (bearish)'
    },
    {
      id: 'screener_movement_neg_25',
      name: '-25% Movement',
      category: 'Screener > Direction & Movement',
      module: 'Screener',
      component: 'Movement Selector',
      description: 'Tests -25% underlying stock movement scenario (bearish)'
    },
    {
      id: 'screener_movement_neg_50',
      name: '-50% Movement',
      category: 'Screener > Direction & Movement',
      module: 'Screener',
      component: 'Movement Selector',
      description: 'Tests -50% underlying stock movement scenario (bearish)'
    },
    {
      id: 'screener_multi_ticker',
      name: 'Multiple Tickers',
      category: 'Screener > Direction & Movement',
      module: 'Screener',
      component: 'Ticker Input',
      description: 'Tests screening with multiple stock tickers (AAPL, MSFT, GOOGL)'
    },

    // ============================================
    // SCREENER MODULE - INTERMEDIATE MODE
    // ============================================
    {
      id: 'screener_intermediate_ultra_short',
      name: 'Ultra Short-Term',
      category: 'Screener > Intermediate Mode',
      module: 'Screener',
      component: 'Intermediate Expiration',
      description: 'Tests Ultra Short-Term expiration preset (21 days, <1 month)'
    },
    {
      id: 'screener_intermediate_short',
      name: 'Short-Term',
      category: 'Screener > Intermediate Mode',
      module: 'Screener',
      component: 'Intermediate Expiration',
      description: 'Tests Short-Term expiration preset (60 days, 1-3 months)'
    },
    {
      id: 'screener_intermediate_medium',
      name: 'Medium-Term',
      category: 'Screener > Intermediate Mode',
      module: 'Screener',
      component: 'Intermediate Expiration',
      description: 'Tests Medium-Term expiration preset (135 days, 3-6 months)'
    },
    {
      id: 'screener_intermediate_long',
      name: 'Long-Term',
      category: 'Screener > Intermediate Mode',
      module: 'Screener',
      component: 'Intermediate Expiration',
      description: 'Tests Long-Term expiration preset (270 days, 6-12 months)'
    },
    {
      id: 'screener_intermediate_leaps',
      name: 'LEAPS',
      category: 'Screener > Intermediate Mode',
      module: 'Screener',
      component: 'Intermediate Expiration',
      description: 'Tests LEAPS expiration preset (450 days, 12+ months)'
    },
    {
      id: 'screener_intermediate_deep_itm',
      name: 'Deep ITM Strike',
      category: 'Screener > Intermediate Mode',
      module: 'Screener',
      component: 'Intermediate Strike',
      description: 'Tests Deep ITM strike preset (-50% from current price)'
    },
    {
      id: 'screener_intermediate_atm',
      name: 'ATM Strike',
      category: 'Screener > Intermediate Mode',
      module: 'Screener',
      component: 'Intermediate Strike',
      description: 'Tests ATM strike preset (at current price)'
    },
    {
      id: 'screener_intermediate_otm',
      name: 'OTM Strike',
      category: 'Screener > Intermediate Mode',
      module: 'Screener',
      component: 'Intermediate Strike',
      description: 'Tests OTM strike preset (+35% from current price)'
    },
    {
      id: 'screener_intermediate_lottery',
      name: 'Lottery Strike',
      category: 'Screener > Intermediate Mode',
      module: 'Screener',
      component: 'Intermediate Strike',
      description: 'Tests Lottery strike preset (+100% from current price)'
    },
    {
      id: 'screener_intermediate_bullish',
      name: 'Bullish Direction',
      category: 'Screener > Intermediate Mode',
      module: 'Screener',
      component: 'Intermediate Direction',
      description: 'Tests bullish direction generates call options with positive stock movement'
    },
    {
      id: 'screener_intermediate_bearish',
      name: 'Bearish Direction',
      category: 'Screener > Intermediate Mode',
      module: 'Screener',
      component: 'Intermediate Direction',
      description: 'Tests bearish direction generates put options with negative stock movement'
    },
    {
      id: 'screener_intermediate_multi_select',
      name: 'Multi-Select Scenarios',
      category: 'Screener > Intermediate Mode',
      module: 'Screener',
      component: 'Intermediate Multi-Select',
      description: 'Tests multi-select generates correct number of scenario combinations'
    },
    {
      id: 'screener_intermediate_combination',
      name: 'Full Combination',
      category: 'Screener > Intermediate Mode',
      module: 'Screener',
      component: 'Intermediate Combination',
      description: 'Tests full combination with all preset categories (expiration + strike + speed + move)'
    },

    // NOTE: Screener Queue tests removed - test_screener_queue.py was never implemented

    // ============================================
    // SCREENER STORIES VIEW MODULE
    // ============================================
    {
      id: 'stories_recalculate_single',
      name: 'Recalculate Single Contract',
      category: 'Screener > Stories View',
      module: 'Screener',
      component: 'ContractCard',
      description: 'Tests /api/recalculate-contract/ endpoint with valid contract parameters for slider updates'
    },
    {
      id: 'stories_recalculate_invalid',
      name: 'Recalculate Invalid Ticker',
      category: 'Screener > Stories View',
      module: 'Screener',
      component: 'ContractCard',
      description: 'Verifies error handling when recalculating contract with invalid ticker symbol'
    },
    {
      id: 'stories_recalculate_missing_params',
      name: 'Recalculate Missing Params',
      category: 'Screener > Stories View',
      module: 'Screener',
      component: 'ContractCard',
      description: 'Tests validation for missing required parameters in recalculate request'
    },
    {
      id: 'stories_recalculate_response',
      name: 'Recalculate Response Format',
      category: 'Screener > Stories View',
      module: 'Screener',
      component: 'ContractCard',
      description: 'Validates response contains premium_gain_pct, simulated_premium, data_source, current_underlying'
    },
    {
      id: 'stories_grouping_data',
      name: 'Results Grouping Fields',
      category: 'Screener > Stories View',
      module: 'Screener',
      component: 'MobileResultsView',
      description: 'Verifies screener results include Ticker, Expiration, Strike for groupResultsByTicker()'
    },
    {
      id: 'stories_simulation_fields',
      name: 'Results Simulation Fields',
      category: 'Screener > Stories View',
      module: 'Screener',
      component: 'MobileResultsView',
      description: 'Verifies results contain Days to Gain, Underlying Gain %, Premium % Gain for Reset functionality'
    },
    {
      id: 'stories_batch_update',
      name: 'Batch Update All Contracts',
      category: 'Screener > Stories View',
      module: 'Screener',
      component: 'ContractCard',
      description: 'Tests Update All functionality by recalculating multiple contracts sequentially'
    },
    {
      id: 'stories_position_data',
      name: 'Position Preservation Data',
      category: 'Screener > Stories View',
      module: 'Screener',
      component: 'TickerStoryView',
      description: 'Verifies results have unique identifiers (ticker+strike+expiration) for position tracking after refresh'
    },
    {
      id: 'stories_original_settings',
      name: 'Original Settings Capture',
      category: 'Screener > Stories View',
      module: 'Screener',
      component: 'MobileResultsView',
      description: 'Tests that original queue settings are available in results for Reset button functionality'
    },

    // ============================================
    // SCREENER STORIES VIEW - SIMULATION SETTINGS (Bug Fixes)
    // ============================================
    {
      id: 'stories_reset_to_queue_values',
      name: 'Reset to Queue Values',
      category: 'Screener > Stories View',
      module: 'Screener',
      component: 'GlobalSimulationControls',
      description: 'Verifies Reset returns simulation to original queue values (not hardcoded 45d/10% defaults)'
    },
    {
      id: 'stories_expiration_settings_initialized',
      name: 'Expiration Settings Initialized',
      category: 'Screener > Stories View',
      module: 'Screener',
      component: 'MobileResultsView',
      description: 'Verifies new expiration dates are initialized with queue values (not hardcoded defaults)'
    },
    {
      id: 'stories_different_gain_values',
      name: 'Negative Gain Preserved',
      category: 'Screener > Stories View',
      module: 'Screener',
      component: 'ContractCard',
      description: 'Verifies negative (bearish) underlying gain values are preserved correctly for Reset'
    },
    {
      id: 'stories_recalculate_with_custom_settings',
      name: 'Recalculate Custom Settings',
      category: 'Screener > Stories View',
      module: 'Screener',
      component: 'ContractCard',
      description: 'Verifies recalculate API accepts custom days and gain values from sliders'
    },

    // ============================================
    // DASHBOARD BUILDER MODULE - DASHBOARD CRUD
    // ============================================
    {
      id: 'dashboard_create',
      name: 'Create Dashboard',
      category: 'Dashboard Builder > Dashboard CRUD',
      module: 'Dashboard Builder',
      component: 'Dashboard Management',
      description: 'Tests creating a new dashboard with name and description'
    },
    {
      id: 'dashboard_list',
      name: 'List Dashboards',
      category: 'Dashboard Builder > Dashboard CRUD',
      module: 'Dashboard Builder',
      component: 'Dashboard Management',
      description: 'Tests listing all dashboards for the authenticated user'
    },
    {
      id: 'dashboard_update',
      name: 'Update Dashboard',
      category: 'Dashboard Builder > Dashboard CRUD',
      module: 'Dashboard Builder',
      component: 'Dashboard Management',
      description: 'Tests updating dashboard name and description'
    },
    {
      id: 'dashboard_delete',
      name: 'Delete Dashboard',
      category: 'Dashboard Builder > Dashboard CRUD',
      module: 'Dashboard Builder',
      component: 'Dashboard Management',
      description: 'Tests deleting a dashboard and verifying removal'
    },

    // ============================================
    // DASHBOARD BUILDER MODULE - WIDGET CRUD
    // ============================================
    {
      id: 'widget_create_simple',
      name: 'Create Simple Widget',
      category: 'Dashboard Builder > Widget CRUD',
      module: 'Dashboard Builder',
      component: 'Widget Management',
      description: 'Tests creating a basic price chart widget without indicators'
    },
    {
      id: 'widget_create_with_indicators',
      name: 'Create Widget with Indicators',
      category: 'Dashboard Builder > Widget CRUD',
      module: 'Dashboard Builder',
      component: 'Widget Management',
      description: 'Tests creating a widget with MACD, RSI, and Bollinger Bands indicators'
    },
    {
      id: 'widget_update',
      name: 'Update Widget',
      category: 'Dashboard Builder > Widget CRUD',
      module: 'Dashboard Builder',
      component: 'Widget Management',
      description: 'Tests updating widget ticker and configuration'
    },
    {
      id: 'widget_delete',
      name: 'Delete Widget',
      category: 'Dashboard Builder > Widget CRUD',
      module: 'Dashboard Builder',
      component: 'Widget Management',
      description: 'Tests deleting a widget from a dashboard'
    },
    {
      id: 'widget_position_update',
      name: 'Update Widget Positions',
      category: 'Dashboard Builder > Widget CRUD',
      module: 'Dashboard Builder',
      component: 'Widget Management',
      description: 'Tests bulk updating widget grid positions (drag-and-drop)'
    },

    // ============================================
    // DASHBOARD BUILDER MODULE - CHART DATA
    // ============================================
    {
      id: 'chart_data_fetch',
      name: 'Fetch Basic Chart Data',
      category: 'Dashboard Builder > Chart Data',
      module: 'Dashboard Builder',
      component: 'Chart Data API',
      description: 'Tests fetching historical price data without indicators'
    },
    {
      id: 'chart_data_with_macd',
      name: 'MACD Indicator',
      category: 'Dashboard Builder > Chart Data',
      module: 'Dashboard Builder',
      component: 'Technical Indicators',
      description: 'Tests MACD calculation (12,26,9 periods)'
    },
    {
      id: 'chart_data_with_rsi',
      name: 'RSI Indicator',
      category: 'Dashboard Builder > Chart Data',
      module: 'Dashboard Builder',
      component: 'Technical Indicators',
      description: 'Tests RSI calculation (14-period) and validates 0-100 range'
    },
    {
      id: 'chart_data_with_bollinger',
      name: 'Bollinger Bands',
      category: 'Dashboard Builder > Chart Data',
      module: 'Dashboard Builder',
      component: 'Technical Indicators',
      description: 'Tests Bollinger Bands calculation (20-period, 2 std dev)'
    },
    {
      id: 'chart_data_all_indicators',
      name: 'All Indicators Combined',
      category: 'Dashboard Builder > Chart Data',
      module: 'Dashboard Builder',
      component: 'Technical Indicators',
      description: 'Tests fetching data with MACD, RSI, and Bollinger Bands together'
    },

    // ============================================
    // DASHBOARD BUILDER MODULE - PERMISSIONS
    // ============================================
    {
      id: 'dashboard_permissions',
      name: 'Dashboard Permissions',
      category: 'Dashboard Builder > Security',
      module: 'Dashboard Builder',
      component: 'Permissions',
      description: 'Tests that users can only access their own dashboards'
    },

    // ============================================
    // DASHBOARD BUILDER MODULE - WIDGET HEIGHT TESTS
    // ============================================
    {
      id: 'widget_height_aapl_all_indicators',
      name: 'Widget Height - AAPL All Indicators',
      category: 'Dashboard Builder > Widget Height',
      module: 'Dashboard Builder',
      component: 'Widget Sizing',
      description: 'Tests AAPL widget height with MACD, RSI, and Bollinger Bands ensures 800px minimum for visibility'
    },
    {
      id: 'widget_height_amd_all_indicators',
      name: 'Widget Height - AMD All Indicators',
      category: 'Dashboard Builder > Widget Height',
      module: 'Dashboard Builder',
      component: 'Widget Sizing',
      description: 'Tests AMD widget height with all indicators includes data validation and 880px minimum'
    },
    {
      id: 'widget_height_single_indicator',
      name: 'Widget Height - Single Indicator',
      category: 'Dashboard Builder > Widget Height',
      module: 'Dashboard Builder',
      component: 'Widget Sizing',
      description: 'Tests widget height with single indicator (MACD only) ensures 560px minimum'
    },
    {
      id: 'widget_height_no_indicators',
      name: 'Widget Height - No Indicators',
      category: 'Dashboard Builder > Widget Height',
      module: 'Dashboard Builder',
      component: 'Widget Sizing',
      description: 'Tests widget height with no indicators (price only) validates base height calculation'
    },

    // ============================================
    // DASHBOARD BUILDER - TICKER TEMPLATE TESTS
    // ============================================
    {
      id: 'ticker_template_set',
      name: 'Ticker Template - Set as Template',
      category: 'Dashboard Builder > Ticker Template',
      module: 'Dashboard Builder',
      component: 'Ticker Template System',
      description: 'Tests setting a dashboard as the ticker template, verifies is_ticker_template flag is set'
    },
    {
      id: 'ticker_template_unset_others',
      name: 'Ticker Template - Unset Previous Templates',
      category: 'Dashboard Builder > Ticker Template',
      module: 'Dashboard Builder',
      component: 'Ticker Template System',
      description: 'Tests that setting a new template automatically unsets the previous template (only one template allowed)'
    },
    {
      id: 'ticker_template_create_dynamic_widget',
      name: 'Ticker Template - Create Dynamic Widget',
      category: 'Dashboard Builder > Ticker Template',
      module: 'Dashboard Builder',
      component: 'Dynamic Widgets',
      description: 'Tests creating a widget with use_dynamic_ticker=True, verifies it will follow clicked tickers'
    },
    {
      id: 'ticker_template_create_static_widget',
      name: 'Ticker Template - Create Static Widget',
      category: 'Dashboard Builder > Ticker Template',
      module: 'Dashboard Builder',
      component: 'Static Widgets',
      description: 'Tests creating a widget with use_dynamic_ticker=False, verifies it stays static (like SPY, VIX)'
    },
    {
      id: 'ticker_template_get_dashboard',
      name: 'Ticker Template - Get Ticker Dashboard',
      category: 'Dashboard Builder > Ticker Template',
      module: 'Dashboard Builder',
      component: 'Ticker Dashboard API',
      description: 'Tests fetching ticker-specific dashboard, verifies dynamic widgets show requested ticker and static widgets remain unchanged'
    },
    {
      id: 'ticker_template_default_creation',
      name: 'Ticker Template - Default Template Creation',
      category: 'Dashboard Builder > Ticker Template',
      module: 'Dashboard Builder',
      component: 'Default Template',
      description: 'Tests that default template is auto-created with 3 widgets (clicked ticker + SPY + VIX) if no template exists'
    },
    {
      id: 'ticker_template_dynamic_replacement',
      name: 'Ticker Template - Dynamic Ticker Replacement',
      category: 'Dashboard Builder > Ticker Template',
      module: 'Dashboard Builder',
      component: 'Dynamic Replacement',
      description: 'Tests multiple ticker requests on same template (TSLA, AMD), verifies each shows correct ticker'
    },
    {
      id: 'ticker_template_mixed_widgets',
      name: 'Ticker Template - Mixed Dynamic/Static Widgets',
      category: 'Dashboard Builder > Ticker Template',
      module: 'Dashboard Builder',
      component: 'Mixed Template',
      description: 'Tests comprehensive template with mix of dynamic widget (all indicators) and static widgets (SPY with MACD, VIX no indicators)'
    },

    // ============================================
    // DASHBOARD BUILDER - WIDGET LOCK/UNLOCK TESTS
    // ============================================
    {
      id: 'widget_lock_toggle',
      name: 'Widget Lock/Unlock Toggle',
      category: 'Dashboard Builder > Lock/Unlock',
      module: 'Dashboard Builder',
      component: 'Widget Lock',
      description: 'Tests toggling widget lock state from locked (default) to unlocked and back to locked'
    },
    {
      id: 'widget_lock_persistence',
      name: 'Widget Lock State Persistence',
      category: 'Dashboard Builder > Lock/Unlock',
      module: 'Dashboard Builder',
      component: 'Widget Lock',
      description: 'Tests that lock state persists correctly across multiple GET requests after unlocking and locking'
    },
    {
      id: 'widget_lock_default_state',
      name: 'Widget Lock Default State',
      category: 'Dashboard Builder > Lock/Unlock',
      module: 'Dashboard Builder',
      component: 'Widget Lock',
      description: 'Tests that newly created widgets default to locked state (use_dynamic_ticker=False)'
    },
    {
      id: 'widget_bulk_ticker_update',
      name: 'Widget Bulk Ticker Update (Unlocked Only)',
      category: 'Dashboard Builder > Lock/Unlock',
      module: 'Dashboard Builder',
      component: 'Global Ticker Search',
      description: 'Tests bulk ticker update functionality - updates only unlocked widgets, locked widgets remain unchanged'
    },
    {
      id: 'widget_individual_ticker_update',
      name: 'Widget Individual Ticker Update',
      category: 'Dashboard Builder > Lock/Unlock',
      module: 'Dashboard Builder',
      component: 'Widget Edit',
      description: 'Tests individual widget ticker update without affecting lock state'
    },

    // ============================================
    // DASHBOARD BUILDER - OPTIONS PREMIUM CHART TESTS
    // ============================================
    {
      id: 'options_historical_fetch',
      name: 'Options Historical Fetch',
      category: 'Dashboard Builder > Options Premium Chart',
      module: 'Dashboard Builder',
      component: 'Options Chart',
      description: 'Tests basic options historical data fetch for a call option'
    },
    {
      id: 'options_historical_put',
      name: 'Options Historical Put',
      category: 'Dashboard Builder > Options Premium Chart',
      module: 'Dashboard Builder',
      component: 'Options Chart',
      description: 'Tests options historical data fetch for a put option'
    },
    {
      id: 'options_historical_date_ranges',
      name: 'Options Historical Date Ranges',
      category: 'Dashboard Builder > Options Premium Chart',
      module: 'Dashboard Builder',
      component: 'Options Chart',
      description: 'Tests different date range parameters (1m, 3m, 6m, 1y)'
    },
    {
      id: 'options_historical_invalid_type',
      name: 'Options Historical Invalid Type',
      category: 'Dashboard Builder > Options Premium Chart',
      module: 'Dashboard Builder',
      component: 'Options Chart',
      description: 'Tests validation: option_type must be call or put'
    },
    {
      id: 'options_historical_invalid_strike',
      name: 'Options Historical Invalid Strike',
      category: 'Dashboard Builder > Options Premium Chart',
      module: 'Dashboard Builder',
      component: 'Options Chart',
      description: 'Tests validation: strike must be a valid number'
    },
    {
      id: 'options_historical_invalid_expiration',
      name: 'Options Historical Invalid Expiration',
      category: 'Dashboard Builder > Options Premium Chart',
      module: 'Dashboard Builder',
      component: 'Options Chart',
      description: 'Tests validation: expiration must be in YYYY-MM-DD format'
    },

    // ============================================
    // TECHNICAL INDICATORS - MACD UNIT TESTS
    // ============================================
    {
      id: 'macd_basic_calculation',
      name: 'MACD Basic Calculation',
      category: 'Technical Indicators > MACD',
      module: 'Technical Indicators',
      component: 'MACD',
      description: 'Tests basic MACD calculation with sufficient data (60 days)'
    },
    {
      id: 'macd_standard_params',
      name: 'MACD Standard Parameters',
      category: 'Technical Indicators > MACD',
      module: 'Technical Indicators',
      component: 'MACD',
      description: 'Validates MACD with standard parameters (12, 26, 9)'
    },
    {
      id: 'macd_custom_params',
      name: 'MACD Custom Parameters',
      category: 'Technical Indicators > MACD',
      module: 'Technical Indicators',
      component: 'MACD',
      description: 'Tests MACD with custom fast/slow/signal periods'
    },
    {
      id: 'macd_insufficient_data',
      name: 'MACD Insufficient Data',
      category: 'Technical Indicators > MACD',
      module: 'Technical Indicators',
      component: 'MACD',
      description: 'Tests MACD handling when data is less than slow period'
    },
    {
      id: 'macd_flat_prices',
      name: 'MACD Flat Prices',
      category: 'Technical Indicators > MACD',
      module: 'Technical Indicators',
      component: 'MACD',
      description: 'Validates MACD behavior with unchanging prices (should be ~0)'
    },
    {
      id: 'macd_trending_up',
      name: 'MACD Uptrend Detection',
      category: 'Technical Indicators > MACD',
      module: 'Technical Indicators',
      component: 'MACD',
      description: 'Tests MACD correctly identifies upward trending markets'
    },
    {
      id: 'macd_trending_down',
      name: 'MACD Downtrend Detection',
      category: 'Technical Indicators > MACD',
      module: 'Technical Indicators',
      component: 'MACD',
      description: 'Tests MACD correctly identifies downward trending markets'
    },
    {
      id: 'macd_volatile_data',
      name: 'MACD Volatile Data',
      category: 'Technical Indicators > MACD',
      module: 'Technical Indicators',
      component: 'MACD',
      description: 'Tests MACD with highly volatile price movements'
    },
    {
      id: 'macd_with_nan',
      name: 'MACD NaN Handling',
      category: 'Technical Indicators > MACD',
      module: 'Technical Indicators',
      component: 'MACD',
      description: 'Tests MACD calculation with NaN values in price data'
    },
    {
      id: 'macd_signal_crossover',
      name: 'MACD Signal Crossover',
      category: 'Technical Indicators > MACD',
      module: 'Technical Indicators',
      component: 'MACD',
      description: 'Tests detection of MACD-signal line crossovers (buy/sell signals)'
    },
    {
      id: 'macd_histogram_validation',
      name: 'MACD Histogram Validation',
      category: 'Technical Indicators > MACD',
      module: 'Technical Indicators',
      component: 'MACD',
      description: 'Validates histogram equals MACD - Signal'
    },
    {
      id: 'macd_known_values',
      name: 'MACD Known Values',
      category: 'Technical Indicators > MACD',
      module: 'Technical Indicators',
      component: 'MACD',
      description: 'Tests MACD against predictable data patterns'
    },

    // ============================================
    // TECHNICAL INDICATORS - RSI UNIT TESTS
    // ============================================
    {
      id: 'rsi_basic_calculation',
      name: 'RSI Basic Calculation',
      category: 'Technical Indicators > RSI',
      module: 'Technical Indicators',
      component: 'RSI',
      description: 'Tests basic RSI calculation with 40 days of data'
    },
    {
      id: 'rsi_range_validation',
      name: 'RSI Range Validation',
      category: 'Technical Indicators > RSI',
      module: 'Technical Indicators',
      component: 'RSI',
      description: 'Validates RSI values are always between 0 and 100'
    },
    {
      id: 'rsi_overbought',
      name: 'RSI Overbought Detection',
      category: 'Technical Indicators > RSI',
      module: 'Technical Indicators',
      component: 'RSI',
      description: 'Tests RSI correctly identifies overbought conditions (>70)'
    },
    {
      id: 'rsi_oversold',
      name: 'RSI Oversold Detection',
      category: 'Technical Indicators > RSI',
      module: 'Technical Indicators',
      component: 'RSI',
      description: 'Tests RSI correctly identifies oversold conditions (<30)'
    },
    {
      id: 'rsi_neutral_zone',
      name: 'RSI Neutral Zone',
      category: 'Technical Indicators > RSI',
      module: 'Technical Indicators',
      component: 'RSI',
      description: 'Tests RSI behavior in neutral market (30-70 range)'
    },
    {
      id: 'rsi_uptrend',
      name: 'RSI Uptrend Behavior',
      category: 'Technical Indicators > RSI',
      module: 'Technical Indicators',
      component: 'RSI',
      description: 'Validates RSI stays above 50 in uptrending markets'
    },
    {
      id: 'rsi_downtrend',
      name: 'RSI Downtrend Behavior',
      category: 'Technical Indicators > RSI',
      module: 'Technical Indicators',
      component: 'RSI',
      description: 'Validates RSI stays below 50 in downtrending markets'
    },
    {
      id: 'rsi_flat_prices',
      name: 'RSI Flat Prices',
      category: 'Technical Indicators > RSI',
      module: 'Technical Indicators',
      component: 'RSI',
      description: 'Tests RSI handling with unchanging prices'
    },
    {
      id: 'rsi_insufficient_data',
      name: 'RSI Insufficient Data',
      category: 'Technical Indicators > RSI',
      module: 'Technical Indicators',
      component: 'RSI',
      description: 'Tests RSI with less than 14 periods of data'
    },
    {
      id: 'rsi_with_nan',
      name: 'RSI NaN Handling',
      category: 'Technical Indicators > RSI',
      module: 'Technical Indicators',
      component: 'RSI',
      description: 'Tests RSI calculation with NaN values in price data'
    },
    {
      id: 'rsi_volatile_data',
      name: 'RSI Volatile Data',
      category: 'Technical Indicators > RSI',
      module: 'Technical Indicators',
      component: 'RSI',
      description: 'Tests RSI with highly volatile price movements'
    },
    {
      id: 'rsi_known_values',
      name: 'RSI Known Values',
      category: 'Technical Indicators > RSI',
      module: 'Technical Indicators',
      component: 'RSI',
      description: 'Tests RSI against known behavior (consistent gains/losses)'
    },

    // ============================================
    // TECHNICAL INDICATORS - BOLLINGER BANDS
    // ============================================
    {
      id: 'bb_basic_calculation',
      name: 'BB Basic Calculation',
      category: 'Technical Indicators > Bollinger Bands',
      module: 'Technical Indicators',
      component: 'Bollinger Bands',
      description: 'Tests basic Bollinger Bands calculation with 50 days data'
    },
    {
      id: 'bb_band_relationship',
      name: 'BB Band Relationship',
      category: 'Technical Indicators > Bollinger Bands',
      module: 'Technical Indicators',
      component: 'Bollinger Bands',
      description: 'Validates upper >= middle >= lower band relationship'
    },
    {
      id: 'bb_standard_params',
      name: 'BB Standard Parameters',
      category: 'Technical Indicators > Bollinger Bands',
      module: 'Technical Indicators',
      component: 'Bollinger Bands',
      description: 'Tests Bollinger Bands with standard parameters (20, 2)'
    },
    {
      id: 'bb_custom_params',
      name: 'BB Custom Parameters',
      category: 'Technical Indicators > Bollinger Bands',
      module: 'Technical Indicators',
      component: 'Bollinger Bands',
      description: 'Tests Bollinger Bands with custom period and std dev'
    },
    {
      id: 'bb_flat_prices',
      name: 'BB Flat Prices',
      category: 'Technical Indicators > Bollinger Bands',
      module: 'Technical Indicators',
      component: 'Bollinger Bands',
      description: 'Tests BB with unchanging prices (bands should converge)'
    },
    {
      id: 'bb_high_volatility',
      name: 'BB High Volatility',
      category: 'Technical Indicators > Bollinger Bands',
      module: 'Technical Indicators',
      component: 'Bollinger Bands',
      description: 'Tests BB width expansion with high volatility'
    },
    {
      id: 'bb_low_volatility',
      name: 'BB Low Volatility',
      category: 'Technical Indicators > Bollinger Bands',
      module: 'Technical Indicators',
      component: 'Bollinger Bands',
      description: 'Tests BB width contraction with low volatility'
    },
    {
      id: 'bb_insufficient_data',
      name: 'BB Insufficient Data',
      category: 'Technical Indicators > Bollinger Bands',
      module: 'Technical Indicators',
      component: 'Bollinger Bands',
      description: 'Tests BB with less than 20 periods of data'
    },
    {
      id: 'bb_with_nan',
      name: 'BB NaN Handling',
      category: 'Technical Indicators > Bollinger Bands',
      module: 'Technical Indicators',
      component: 'Bollinger Bands',
      description: 'Tests BB calculation with NaN values in price data'
    },
    {
      id: 'bb_width_calculation',
      name: 'BB Width Calculation',
      category: 'Technical Indicators > Bollinger Bands',
      module: 'Technical Indicators',
      component: 'Bollinger Bands',
      description: 'Tests BB width responds correctly to volatility changes'
    },
    {
      id: 'bb_price_position',
      name: 'BB Price Position',
      category: 'Technical Indicators > Bollinger Bands',
      module: 'Technical Indicators',
      component: 'Bollinger Bands',
      description: 'Validates ~95% of prices stay within 2 std dev bands'
    },
    {
      id: 'bb_known_values',
      name: 'BB Known Values',
      category: 'Technical Indicators > Bollinger Bands',
      module: 'Technical Indicators',
      component: 'Bollinger Bands',
      description: 'Tests BB against known calculations (SMA and std dev)'
    },

    // ============================================
    // TECHNICAL INDICATORS - COMBINED TESTS
    // ============================================
    {
      id: 'all_indicators_basic',
      name: 'All Indicators Basic',
      category: 'Technical Indicators > Combined',
      module: 'Technical Indicators',
      component: 'Combined Indicators',
      description: 'Tests calculating MACD, RSI, and Bollinger Bands together'
    },
    {
      id: 'all_indicators_edge_cases',
      name: 'All Indicators Edge Cases',
      category: 'Technical Indicators > Combined',
      module: 'Technical Indicators',
      component: 'Combined Indicators',
      description: 'Tests all indicators with flat, linear, and random data'
    },
    {
      id: 'all_indicators_large_dataset',
      name: 'All Indicators Large Dataset',
      category: 'Technical Indicators > Combined',
      module: 'Technical Indicators',
      component: 'Combined Indicators',
      description: 'Performance test with 500 days of data (<2s expected)'
    },

    // ============================================
    // TECHNICAL INDICATORS - HELPER FUNCTIONS
    // ============================================
    {
      id: 'ema_calculation',
      name: 'EMA Calculation',
      category: 'Technical Indicators > Helpers',
      module: 'Technical Indicators',
      component: 'Helper Functions',
      description: 'Tests Exponential Moving Average calculation'
    },
    {
      id: 'sma_calculation',
      name: 'SMA Calculation',
      category: 'Technical Indicators > Helpers',
      module: 'Technical Indicators',
      component: 'Helper Functions',
      description: 'Tests Simple Moving Average calculation'
    },

    // ============================================
    // TECHNICAL INDICATORS - REAL WORLD TESTS
    // ============================================
    {
      id: 'macd_real_world_stocks',
      name: 'MACD Real World (1yr)',
      category: 'Technical Indicators > Real World',
      module: 'Technical Indicators',
      component: 'MACD',
      description: 'Tests MACD with 1 year of real data from AAPL, MSFT, GOOG, AMD, GM (~252 trading days each)'
    },
    {
      id: 'rsi_real_world_stocks',
      name: 'RSI Real World (1yr)',
      category: 'Technical Indicators > Real World',
      module: 'Technical Indicators',
      component: 'RSI',
      description: 'Tests RSI with 1 year of real data from AAPL, MSFT, GOOG, AMD, GM (~252 trading days each)'
    },
    {
      id: 'bb_real_world_stocks',
      name: 'BB Real World (1yr)',
      category: 'Technical Indicators > Real World',
      module: 'Technical Indicators',
      component: 'Bollinger Bands',
      description: 'Tests Bollinger Bands with 1 year of real data from AAPL, MSFT, GOOG, AMD, GM (~252 trading days each)'
    },
    {
      id: 'all_indicators_real_world',
      name: 'All Indicators Real World (1yr)',
      category: 'Technical Indicators > Real World',
      module: 'Technical Indicators',
      component: 'Combined Indicators',
      description: 'Tests MACD, RSI, and Bollinger Bands together with 1 year of real stock data'
    },
    {
      id: 'real_world_performance',
      name: 'Real World Performance Test',
      category: 'Technical Indicators > Real World',
      module: 'Technical Indicators',
      component: 'Performance',
      description: 'Performance test: Calculate all indicators for 5 stocks with 1 year data (<15s expected)'
    },

    // Cache Persistence Tests
    {
      id: 'cache_screener_save',
      name: 'Screener Cache Save',
      category: 'Cache Persistence',
      module: 'Cache',
      component: 'Screener',
      description: 'Verifies screener results are properly structured for localStorage caching (JSON serialization)'
    },
    {
      id: 'cache_screener_restore',
      name: 'Screener Cache Restore',
      category: 'Cache Persistence',
      module: 'Cache',
      component: 'Screener',
      description: 'Tests screener cache restoration logic (round-trip save/restore)'
    },
    {
      id: 'cache_screener_expiration',
      name: 'Screener Cache Expiration',
      category: 'Cache Persistence',
      module: 'Cache',
      component: 'Screener',
      description: 'Tests screener cache expiration logic (24-hour TTL)'
    },
    {
      id: 'cache_watchlist_save',
      name: 'Watchlist Cache Save',
      category: 'Cache Persistence',
      module: 'Cache',
      component: 'Watchlist',
      description: 'Verifies watchlist data is properly structured for localStorage caching'
    },
    {
      id: 'cache_watchlist_restore',
      name: 'Watchlist Cache Restore',
      category: 'Cache Persistence',
      module: 'Cache',
      component: 'Watchlist',
      description: 'Tests watchlist cache restoration logic (round-trip save/restore)'
    },
    {
      id: 'cache_watchlist_expiration',
      name: 'Watchlist Cache Expiration',
      category: 'Cache Persistence',
      module: 'Cache',
      component: 'Watchlist',
      description: 'Tests watchlist cache expiration logic (30-minute TTL)'
    },
    {
      id: 'cache_screener_structure',
      name: 'Screener Cache Structure',
      category: 'Cache Persistence',
      module: 'Cache',
      component: 'Screener',
      description: 'Validates screener cache uses correct localStorage keys (screener_last_results, screener_last_results_timestamp)'
    },
    {
      id: 'cache_watchlist_structure',
      name: 'Watchlist Cache Structure',
      category: 'Cache Persistence',
      module: 'Cache',
      component: 'Watchlist',
      description: 'Validates watchlist cache uses correct localStorage keys (watchlist_last_data, watchlist_last_data_timestamp)'
    },

    // Cache Persistence - Group Collapse State
    {
      id: 'cache_collapsed_groups_structure',
      name: 'Collapsed Groups Structure',
      category: 'Cache Persistence',
      module: 'Cache',
      component: 'Watchlist Groups',
      description: 'Validates collapsed groups cache uses correct localStorage key (watchlist_collapsed_groups) with no timestamp companion'
    },
    {
      id: 'cache_collapsed_groups_save',
      name: 'Collapsed Groups Save',
      category: 'Cache Persistence',
      module: 'Cache',
      component: 'Watchlist Groups',
      description: 'Verifies collapsed groups state ({groupId: boolean}) is properly structured for localStorage caching'
    },
    {
      id: 'cache_collapsed_groups_restore',
      name: 'Collapsed Groups Restore',
      category: 'Cache Persistence',
      module: 'Cache',
      component: 'Watchlist Groups',
      description: 'Tests collapsed groups cache restoration with edge cases (empty state, stale IDs, mixed values, many groups)'
    },
    {
      id: 'cache_collapsed_groups_no_expiration',
      name: 'Collapsed Groups No Expiration',
      category: 'Cache Persistence',
      module: 'Cache',
      component: 'Watchlist Groups',
      description: 'Verifies collapsed groups cache has no TTL — persists indefinitely as a UI preference (unlike 30min watchlist / 24hr screener caches)'
    },

    // ============================================
    // DATA PROVIDERS - POLYGON.IO
    // ============================================
    {
      id: 'provider_polygon_stock_price',
      name: 'Polygon Stock Price',
      category: 'Data Providers',
      module: 'Polygon.io',
      component: 'Stock Prices',
      description: 'Tests Polygon.io real-time stock price retrieval API'
    },
    {
      id: 'provider_polygon_options',
      name: 'Polygon Options Chain',
      category: 'Data Providers',
      module: 'Polygon.io',
      component: 'Options Data',
      description: 'Tests Polygon.io options chain snapshot API with filtering'
    },
    {
      id: 'provider_polygon_normalization',
      name: 'Polygon Data Normalization',
      category: 'Data Providers',
      module: 'Polygon.io',
      component: 'Data Processing',
      description: 'Verifies Polygon.io data is correctly normalized to standard format'
    },
    {
      id: 'provider_polygon_snapshot_access',
      name: 'Polygon Snapshot Entitlements',
      category: 'Data Providers',
      module: 'Polygon.io',
      component: 'Entitlements',
      description: 'Fails if any Massive (Polygon) options or stock snapshot endpoint returns 401/403 (plan or market-data agreement missing); reports whether the data is DELAYED or REAL-TIME'
    },
    {
      id: 'provider_polygon_iv_coverage',
      name: 'Polygon IV/Greeks Coverage',
      category: 'Data Providers',
      module: 'Polygon.io',
      component: 'IV & Greeks',
      description: 'Fails if fewer than 80% of liquid near-ATM AAPL calls (7-45 DTE, OI 100+) have provider or computed IV (computed from the quote midpoint on the individual plan), or if computed IV/greeks fail the sanity and round-trip checks'
    },

    // ============================================
    // DATA PROVIDERS - EODHD
    // ============================================
    {
      id: 'provider_eodhd_stock_price',
      name: 'EODHD Stock Price',
      category: 'Data Providers',
      module: 'EODHD',
      component: 'Stock Prices',
      description: 'Tests EODHD stock price retrieval API (fallback provider)'
    },
    {
      id: 'provider_eodhd_options',
      name: 'EODHD Options Chain',
      category: 'Data Providers',
      module: 'EODHD',
      component: 'Options Data',
      description: 'Tests EODHD options chain API with date/strike filtering'
    },
    {
      id: 'provider_eodhd_single_option',
      name: 'EODHD Single Option',
      category: 'Data Providers',
      module: 'EODHD',
      component: 'Options Data',
      description: 'Tests EODHD single specific option contract lookup'
    },

    // ============================================
    // DATA PROVIDERS - FACTORY & INTEGRATION
    // ============================================
    {
      id: 'provider_factory_selection',
      name: 'Factory Provider Selection',
      category: 'Data Providers',
      module: 'Provider Factory',
      component: 'Factory',
      description: 'Verifies provider factory selects correct provider based on settings'
    },
    {
      id: 'provider_factory_fallback',
      name: 'Factory Automatic Fallback',
      category: 'Data Providers',
      module: 'Provider Factory',
      component: 'Fallback',
      description: 'Tests automatic fallback from Polygon to EODHD when primary fails'
    },
    {
      id: 'provider_screener_integration',
      name: 'Screener Provider Integration',
      category: 'Data Providers',
      module: 'Integration',
      component: 'Screener',
      description: 'Tests screener works correctly with provider abstraction layer'
    },
    {
      id: 'provider_watchlist_integration',
      name: 'Watchlist Provider Integration',
      category: 'Data Providers',
      module: 'Integration',
      component: 'Watchlist',
      description: 'Tests watchlist simulation works with provider abstraction layer'
    },

    // ============================================
    // SCREENER METADATA TESTS (TOOLTIP VERIFICATION)
    // ============================================
    {
      id: 'screener_meta_present',
      name: 'Metadata Present',
      category: 'Screener > Tooltip Metadata',
      module: 'Screener',
      component: 'Tooltip Metadata',
      description: 'Verifies screener results contain _meta object with all required keys (underlying_price, current_premium, implied_volatility, greeks, bid_ask, volume)'
    },
    {
      id: 'screener_meta_structure',
      name: 'Metadata Structure',
      category: 'Screener > Tooltip Metadata',
      module: 'Screener',
      component: 'Tooltip Metadata',
      description: 'Verifies _meta object entries have correct structure with source field and optional timestamp/is_default fields'
    },
    {
      id: 'screener_meta_source_values',
      name: 'Metadata Source Values',
      category: 'Screener > Tooltip Metadata',
      module: 'Screener',
      component: 'Tooltip Metadata',
      description: 'Verifies data_source values are valid: "P" (Polygon.io) or "E" (EODHD)'
    },

    // ============================================
    // PROVIDER PRIORITY TESTS
    // ============================================
    {
      id: 'provider_polygon_priority',
      name: 'Polygon.io Priority',
      category: 'Data Providers > Priority',
      module: 'Provider Factory',
      component: 'Provider Priority',
      description: 'Verifies Polygon.io is configured as the primary data source and used first for data fetching'
    },
    {
      id: 'provider_tracking',
      name: 'Provider Tracking',
      category: 'Data Providers > Priority',
      module: 'Provider Factory',
      component: 'Provider Tracking',
      description: 'Verifies _last_provider_used attribute tracks which provider successfully returned data'
    },

    // ============================================
    // FALLBACK TESTS
    // ============================================
    {
      id: 'provider_eodhd_fallback',
      name: 'EODHD Fallback Available',
      category: 'Data Providers > Fallback',
      module: 'Provider Factory',
      component: 'Fallback Mechanism',
      description: 'Verifies EODHD is properly configured as fallback provider and returns data with "E" source marker'
    },
    {
      id: 'provider_fallback_config',
      name: 'Fallback Configuration',
      category: 'Data Providers > Fallback',
      module: 'Provider Factory',
      component: 'Settings',
      description: 'Verifies OPTIONS_FALLBACK_ENABLED is True and both provider API keys are configured'
    },

    // ============================================
    // TICKER SEARCH MODULE
    // ============================================
    {
      id: 'ticker_database_populated',
      name: 'Ticker Database Populated',
      category: 'Ticker Search',
      module: 'Ticker Search',
      component: 'Database',
      description: 'Verifies ticker database has data (>100 tickers) and major tickers exist (AAPL, MSFT, etc.)'
    },
    {
      id: 'ticker_search_basic',
      name: 'Basic Ticker Search',
      category: 'Ticker Search',
      module: 'Ticker Search',
      component: 'Search Endpoint',
      description: 'Tests /api/ticker-search/?search=AAPL returns AAPL in results'
    },
    {
      id: 'ticker_search_partial',
      name: 'Partial Ticker Search',
      category: 'Ticker Search',
      module: 'Ticker Search',
      component: 'Search Endpoint',
      description: 'Tests partial search (e.g., "AA" returns AAPL, AAL, etc.)'
    },
    {
      id: 'ticker_search_by_name',
      name: 'Search by Company Name',
      category: 'Ticker Search',
      module: 'Ticker Search',
      component: 'Search Endpoint',
      description: 'Tests searching by company name (e.g., "Apple" returns AAPL)'
    },
    {
      id: 'ticker_search_special_chars',
      name: 'Special Character Tickers',
      category: 'Ticker Search',
      module: 'Ticker Search',
      component: 'Search Endpoint',
      description: 'Tests search for tickers with dots (e.g., VIX returns VIX.INDX)'
    },
    {
      id: 'ticker_search_critical',
      name: 'Critical Tickers Available',
      category: 'Ticker Search',
      module: 'Ticker Search',
      component: 'Search Endpoint',
      description: 'Verifies all critical tickers (UBER, TSLA, AMD, RCAT, U, IREN, VIX.INDX, SPY, NASDX) are searchable'
    },

    // ============================================
    // RESEARCH MODULE
    // ============================================
    {
      id: 'research_fundamentals_basic',
      name: 'Fundamentals Basic',
      category: 'Research',
      module: 'Research',
      component: 'Fundamentals',
      description: 'Tests POST /api/research/fundamentals/ with single ticker (AAPL), verifies response structure and key columns'
    },
    {
      id: 'research_fundamentals_multi',
      name: 'Fundamentals Multi-Ticker',
      category: 'Research',
      module: 'Research',
      component: 'Fundamentals',
      description: 'Tests fundamentals endpoint with multiple tickers (AAPL, MSFT), verifies both results returned'
    },
    {
      id: 'research_fundamentals_validation',
      name: 'Fundamentals Validation',
      category: 'Research',
      module: 'Research',
      component: 'Fundamentals',
      description: 'Tests fundamentals endpoint rejects empty tickers array with 400 error'
    },
    {
      id: 'research_financials_basic',
      name: 'Financials Basic (5yr)',
      category: 'Research',
      module: 'Research',
      component: 'Financials',
      description: 'Tests POST /api/research/financials/ratios/ with 5-year data for AAPL'
    },
    {
      id: 'research_financials_10yr',
      name: 'Financials 10-Year',
      category: 'Research',
      module: 'Research',
      component: 'Financials',
      description: 'Tests financials endpoint with 10-year data range for AAPL'
    },
    {
      id: 'research_financials_validation',
      name: 'Financials Validation',
      category: 'Research',
      module: 'Research',
      component: 'Financials',
      description: 'Tests financials endpoint rejects invalid years value (3) with 400 error'
    },
    {
      id: 'research_financials_structure',
      name: 'Financials Structure',
      category: 'Research',
      module: 'Research',
      component: 'Financials',
      description: 'Verifies financials response has correct structure: tickers, years, ratios (with all metric keys including peg_ratio), ratio_labels, and TTM'
    },
    {
      id: 'research_financials_peg_ratio',
      name: 'Financials PEG Ratio',
      category: 'Research',
      module: 'Research',
      component: 'Financials',
      description: 'Verifies PEG ratio is calculated and present in financials response with non-null values for AAPL'
    },
    {
      id: 'research_financials_multi',
      name: 'Financials Multi-Ticker',
      category: 'Research',
      module: 'Research',
      component: 'Financials',
      description: 'Tests financials endpoint with multiple tickers (AAPL, MSFT), verifies both have revenue data across years'
    },
    {
      id: 'research_eps_basic',
      name: 'EPS Trends Basic',
      category: 'Research',
      module: 'Research',
      component: 'EPS Trends',
      description: 'Tests POST /api/research/eps-trends/ with single ticker (AAPL), verifies eps_trends array returned'
    },
    {
      id: 'research_eps_multi',
      name: 'EPS Trends Multi-Ticker',
      category: 'Research',
      module: 'Research',
      component: 'EPS Trends',
      description: 'Tests EPS trends endpoint with multiple tickers (AAPL, MSFT), verifies data for both tickers'
    },
    {
      id: 'research_eps_validation',
      name: 'EPS Trends Validation',
      category: 'Research',
      module: 'Research',
      component: 'EPS Trends',
      description: 'Tests EPS trends endpoint rejects empty tickers array with 400 error'
    },
    {
      id: 'research_eps_schema',
      name: 'EPS Trends Row Schema',
      category: 'Research',
      module: 'Research',
      component: 'EPS Trends',
      description: 'Verifies every EPS row carries all 4 periods (Curr Qtr, Next Qtr, Curr Yr, Next Yr) x 5 timepoints (Curr, 7, 30, 60, 90) plus 4 Slope keys, and that they are populated. The heatmap builds these keys by string template, so a renamed field renders a blank grid with no error'
    },
    {
      id: 'research_eps_growth_inputs',
      name: 'EPS Expected-Growth Inputs',
      category: 'Research',
      module: 'Research',
      component: 'EPS Trends',
      description: 'Verifies the 4 "<period> Curr" fields the Expected EPS Growth (Current → Next) heatmap divides on are present, numeric and non-null, then runs the frontend growth formula and requires a finite result for both the quarterly and yearly pair'
    },
    {
      id: 'research_eps_json_safe',
      name: 'EPS Trends JSON Safety',
      category: 'Research',
      module: 'Research',
      component: 'EPS Trends',
      description: 'Verifies clean_nan_values() is still covering the pandas DataFrame output: no bare NaN/Infinity literals in the body (JSON.parse would throw and blank the whole Research page) and every EPS value is a finite number or null'
    },
    {
      id: 'research_eps_partial_failure',
      name: 'EPS Trends Partial Failure',
      category: 'Research',
      module: 'Research',
      component: 'EPS Trends',
      description: 'Verifies one unresolvable ticker does not 500 the scan: a valid ticker keeps its full data alongside a failed one, and the failed row degrades to no-data that the growth card reads as "Insufficient data"'
    },

    // ============================================
    // PRODUCTION VALIDATION MODULE
    // ============================================
    {
      id: 'runner_setup',
      name: 'Runner Setup',
      category: 'Production Validation',
      module: 'Production',
      component: 'Test Runner',
      description: 'Runner self-check: passes when the server-side mode, the shared test account credentials, the account itself and (in production) its login all work. Makes no other calls'
    },
    {
      id: 'prod_test_account',
      name: 'Test Account Login',
      category: 'Production Validation',
      module: 'Production',
      component: 'Test Account',
      description: 'Verifies the shared admin-tests account (PROD_TEST_USERNAME) can log in to production and receive a token (read-only)'
    },
    {
      id: 'prod_health_check',
      name: 'Production Health Check',
      category: 'Production Validation',
      module: 'Production',
      component: 'Health',
      description: 'Validates production health endpoint, environment, and database connectivity (read-only)'
    },
    {
      id: 'prod_cors_configuration',
      name: 'CORS Configuration',
      category: 'Production Validation',
      module: 'Production',
      component: 'CORS',
      description: 'Verifies CORS headers are properly configured for production frontend domain (read-only)'
    },
    {
      id: 'prod_database_connectivity',
      name: 'Database Connectivity',
      category: 'Production Validation',
      module: 'Production',
      component: 'Database',
      description: 'Validates production database is accessible and responding (read-only)'
    },
    {
      id: 'prod_authentication_flow',
      name: 'Authentication Flow',
      category: 'Production Validation',
      module: 'Production',
      component: 'Auth',
      description: 'Tests production login/signup endpoints with test account (creates TEST_ prefixed data)'
    },
    {
      id: 'prod_api_endpoints',
      name: 'API Endpoints',
      category: 'Production Validation',
      module: 'Production',
      component: 'APIs',
      description: 'Validates critical API endpoints are accessible in production (read-only)'
    },
    {
      id: 'prod_rate_limiting_configured',
      name: 'Rate Limiting',
      category: 'Production Validation',
      module: 'Production',
      component: 'Security',
      description: 'Verifies rate limiting is active on production endpoints (read-only check)'
    },
    {
      id: 'prod_external_api_access',
      name: 'External API Access',
      category: 'Production Validation',
      module: 'Production',
      component: 'External APIs',
      description: 'Tests production can reach external APIs (EODHD, Polygon) for data (read-only)'
    },
    {
      id: 'prod_create_and_cleanup',
      name: 'Data Create & Cleanup',
      category: 'Production Validation',
      module: 'Production',
      component: 'Data Management',
      description: 'Tests ability to create TEST_ prefixed data and clean it up (creates temporary data)'
    },
    {
      id: 'prod_ssl_certificate',
      name: 'SSL Certificate',
      category: 'Production Validation',
      module: 'Production',
      component: 'SSL',
      description: 'Validates SSL certificate is valid and not expired (read-only)'
    },
    {
      id: 'prod_environment_variables',
      name: 'Environment Variables',
      category: 'Production Validation',
      module: 'Production',
      component: 'Config',
      description: 'Verifies critical environment variables are set correctly (read-only)'
    },

    // ============================================
    // REPORTS ANALYTICS MODULE
    // ============================================
    {
      id: 'reports_active_users',
      name: 'Active Users Time Series',
      category: 'Reports > Analytics',
      module: 'Reports',
      component: 'Active Users',
      description: 'Tests /reports/active-users/ endpoint returns DAU/WAU/MAU time series data with correct structure'
    },
    {
      id: 'reports_feature_adoption',
      name: 'Feature Adoption',
      category: 'Reports > Analytics',
      module: 'Reports',
      component: 'Feature Adoption',
      description: 'Tests /reports/feature-adoption/ endpoint returns action stats, adoption timeline, and total users'
    },
    {
      id: 'reports_conversion_funnel',
      name: 'Conversion Funnel',
      category: 'Reports > Analytics',
      module: 'Reports',
      component: 'Conversion Funnel',
      description: 'Tests /reports/conversion-funnel/ endpoint returns funnel stages (signups/activated/converted) and tier breakdown'
    },
    {
      id: 'reports_retention',
      name: 'Retention Cohorts',
      category: 'Reports > Analytics',
      module: 'Reports',
      component: 'Retention',
      description: 'Tests /reports/retention/ endpoint returns cohort retention data with week-over-week percentages'
    },
    {
      id: 'reports_user_intelligence',
      name: 'User Intelligence',
      category: 'Reports > Analytics',
      module: 'Reports',
      component: 'User Intelligence',
      description: 'Tests /reports/user-intelligence/ endpoint returns user list with engagement scores, statuses, and hot leads'
    },
    {
      id: 'reports_user_summary',
      name: 'User Activity Summary',
      category: 'Reports > Legacy',
      module: 'Reports',
      component: 'User Summary',
      description: 'Tests /reports/user-summary/ endpoint returns activity metrics across 24h/7d/30d/90d periods'
    },
    {
      id: 'reports_tool_usage',
      name: 'Tool Usage Breakdown',
      category: 'Reports > Legacy',
      module: 'Reports',
      component: 'Tool Usage',
      description: 'Tests /reports/tool-usage/ endpoint returns tool breakdown with counts and percentages'
    },
    {
      id: 'reports_user_lookup',
      name: 'User Lookup',
      category: 'Reports > Legacy',
      module: 'Reports',
      component: 'User Lookup',
      description: 'Tests /reports/user-lookup/ endpoint returns detailed user activity profile with tool breakdown'
    },
    {
      id: 'reports_user_journey',
      name: 'User Journey',
      category: 'Reports > Analytics',
      module: 'Reports',
      component: 'User Journey',
      description: 'Tests /reports/user-journey/ endpoint returns session timeline with events grouped by 30-minute gaps'
    },

    // ============================================
    // AFFILIATE PROGRAM MODULE
    // ============================================
    {
      id: 'affiliate_profile_default_code',
      name: 'Profile Has Default Code',
      category: 'Affiliate > Profile',
      module: 'Affiliate',
      component: 'Profile Lifecycle',
      description: 'Tests create_for_user auto-generates a non-custom default AffiliateCode labeled "Default"'
    },
    {
      id: 'affiliate_profile_free_user_blocked',
      name: 'Free User Blocked',
      category: 'Affiliate > Profile',
      module: 'Affiliate',
      component: 'Profile Lifecycle',
      description: 'Tests free-tier users calling GET /affiliate/ receive 404 (no auto-profile for non-paying users)'
    },
    {
      id: 'affiliate_code_create',
      name: 'Create Custom Code',
      category: 'Affiliate > Codes',
      module: 'Affiliate',
      component: 'Campaign Codes',
      description: 'Tests POST /affiliate/codes/ with custom code + label creates an is_custom=True AffiliateCode'
    },
    {
      id: 'affiliate_code_invalid_rejected',
      name: 'Invalid Code Rejected',
      category: 'Affiliate > Codes',
      module: 'Affiliate',
      component: 'Campaign Codes',
      description: 'Tests bad regex returns 400 and duplicate code returns 409 from POST /affiliate/codes/'
    },
    {
      id: 'affiliate_code_rename_once',
      name: 'One-Time Default Rename',
      category: 'Affiliate > Codes',
      module: 'Affiliate',
      component: 'Campaign Codes',
      description: 'Tests POST /affiliate/set-code/ succeeds once then rejects subsequent attempts (is_custom becomes True)'
    },
    {
      id: 'affiliate_referral_apply',
      name: 'Referral Apply + 2yr Lock',
      category: 'Affiliate > Referrals',
      module: 'Affiliate',
      component: 'Referral Attribution',
      description: 'Tests POST /affiliate/apply-code/ creates AffiliateReferral with ~730-day attribution lock'
    },
    {
      id: 'affiliate_referral_self_blocked',
      name: 'Self-Referral Blocked',
      category: 'Affiliate > Referrals',
      module: 'Affiliate',
      component: 'Referral Attribution',
      description: 'Tests affiliate cannot apply their own code (returns 400)'
    },
    {
      id: 'affiliate_commission_year_1',
      name: 'Year 1 Commission (30%)',
      category: 'Affiliate > Commissions',
      module: 'Affiliate',
      component: 'Commission Engine',
      description: 'Tests calculate_affiliate_commission applies 30% rate for fresh (<1yr) referrals'
    },
    {
      id: 'affiliate_commission_year_2',
      name: 'Year 2+ Commission (20%)',
      category: 'Affiliate > Commissions',
      module: 'Affiliate',
      component: 'Commission Engine',
      description: 'Tests calculate_affiliate_commission applies 20% rate for referrals older than 1 year'
    },
    {
      id: 'affiliate_commission_idempotent',
      name: 'Commission Idempotency',
      category: 'Affiliate > Commissions',
      module: 'Affiliate',
      component: 'Commission Engine',
      description: 'Tests replaying the same stripe_invoice_id does not create a duplicate AffiliateCommission row'
    },
    {
      id: 'affiliate_commission_refund_void',
      name: 'Refund Voids Commission',
      category: 'Affiliate > Commissions',
      module: 'Affiliate',
      component: 'Commission Engine',
      description: 'Tests handle_charge_refunded flips a pending commission to status=voided with charge ID in notes'
    },
    {
      id: 'affiliate_admin_list',
      name: 'Admin List Returns default_code',
      category: 'Affiliate > Admin Payouts',
      module: 'Affiliate',
      component: 'Admin Payouts',
      description: 'Tests GET /admin-tests/reports/affiliate-payouts/ returns default_code per row (regression for migration 0018)'
    },
    {
      id: 'affiliate_admin_detail',
      name: 'Admin Detail (No 500)',
      category: 'Affiliate > Admin Payouts',
      module: 'Affiliate',
      component: 'Admin Payouts',
      description: 'Tests GET /admin-tests/reports/affiliate-payouts/:id/ returns 200 (regression for stale affiliate_code field)'
    },
    {
      id: 'affiliate_admin_mark_paid_snapshot',
      name: 'Mark Paid Snapshots Rail',
      category: 'Affiliate > Admin Payouts',
      module: 'Affiliate',
      component: 'Admin Payouts',
      description: 'Tests mark-paid captures paid_method/paid_handle from the profile and the snapshot survives later profile edits'
    },
    {
      id: 'affiliate_admin_void',
      name: 'Void Commission',
      category: 'Affiliate > Admin Payouts',
      module: 'Affiliate',
      component: 'Admin Payouts',
      description: 'Tests POST /admin-tests/reports/affiliate-payouts/void/ flips commission status to voided with reason in notes'
    },

    // ============================================
    // ACCOUNT DELETION MODULE
    // ============================================
    {
      id: 'delete_request_success',
      name: 'Request Deletion Success',
      category: 'Authentication > Account Deletion',
      module: 'Authentication',
      component: 'Account Deletion',
      description: 'Tests successful account deletion request: soft-delete fields set, token revoked, permanent date 90 days out'
    },
    {
      id: 'delete_reinstate',
      name: 'Reinstate Account',
      category: 'Authentication > Account Deletion',
      module: 'Authentication',
      component: 'Account Deletion',
      description: 'Tests reinstating a pending-deletion account: all deletion fields cleared, account fully restored'
    },
    {
      id: 'delete_admin_blocked',
      name: 'Admin Self-Deletion Blocked',
      category: 'Authentication > Account Deletion',
      module: 'Authentication',
      component: 'Account Deletion',
      description: 'Verifies admin users (is_staff=True) cannot delete their own account'
    },
    {
      id: 'delete_wrong_password',
      name: 'Wrong Password Rejected',
      category: 'Authentication > Account Deletion',
      module: 'Authentication',
      component: 'Account Deletion',
      description: 'Tests that incorrect password is rejected when requesting account deletion'
    },
    {
      id: 'delete_active_sub_blocked',
      name: 'Active Subscription Blocked',
      category: 'Authentication > Account Deletion',
      module: 'Authentication',
      component: 'Account Deletion',
      description: 'Verifies deletion is blocked when user has an active paid subscription (must cancel first)'
    },
    {
      id: 'delete_already_pending',
      name: 'Duplicate Request Blocked',
      category: 'Authentication > Account Deletion',
      module: 'Authentication',
      component: 'Account Deletion',
      description: 'Tests that a second deletion request is rejected when one is already pending'
    },
    {
      id: 'delete_login_retention',
      name: 'Login During Retention',
      category: 'Authentication > Account Deletion',
      module: 'Authentication',
      component: 'Account Deletion',
      description: 'Verifies user can still log in during the 90-day retention period after requesting deletion'
    },
    {
      id: 'delete_profile_fields',
      name: 'Profile Shows Deletion',
      category: 'Authentication > Account Deletion',
      module: 'Authentication',
      component: 'Account Deletion',
      description: 'Verifies the profile endpoint returns deletion_scheduled_at and deletion_permanent_at after deletion request'
    },

    // ============================================
    // MACRO DASHBOARD
    // ============================================
    {
      id: 'macro_dashboard_endpoint',
      name: 'Endpoint & Structure',
      category: 'Macro Dashboard',
      module: 'Stock Analyzer / Research',
      component: 'Macro Dashboard',
      description: 'Tests GET /api/stock-analyzer/macro/dashboard/ returns 200 with all sections (fear_greed, market_indices, vix_stats, bitcoin, economic_indicators, fed_watch), errors, and score'
    },
    {
      id: 'macro_section_errors_empty',
      name: 'No Section Errors',
      category: 'Macro Dashboard',
      module: 'Stock Analyzer / Research',
      component: 'Macro Dashboard',
      description: 'Verifies the top-level errors[] is empty — every upstream source loaded. A failure names the section and its upstream error (e.g. missing FRED_API_KEY)'
    },
    {
      id: 'macro_data_freshness',
      name: 'Data Freshness (all cards)',
      category: 'Macro Dashboard',
      module: 'Stock Analyzer / Research',
      component: 'Macro Dashboard',
      description: 'Verifies each card serves the most up-to-date data: indices/VIX/F&G latest date within 7d, Bitcoin within 3d, and at least one economic indicator with a future next_release_date (weekend/holiday tolerant; transient-throttled cards skipped)'
    },
    {
      id: 'macro_fear_greed_cnn',
      name: 'Fear & Greed (CNN)',
      category: 'Macro Dashboard',
      module: 'Stock Analyzer / Research',
      component: 'Fear & Greed',
      description: 'Verifies CNN Fear & Greed: current score in 0-100, rating present, history series non-empty'
    },
    {
      id: 'macro_market_indices_polygon',
      name: 'Market Indices (Polygon)',
      category: 'Macro Dashboard',
      module: 'Stock Analyzer / Research',
      component: 'Market Indices',
      description: 'Verifies Polygon SPY/DIA/QQQ each return a positive current price with history and no per-index error'
    },
    {
      id: 'macro_vix_eodhd',
      name: 'VIX Stats (EODHD)',
      category: 'Macro Dashboard',
      module: 'Stock Analyzer / Research',
      component: 'VIX',
      description: 'Verifies EODHD VIX (VIX.INDX): current value > 0, fresh (current.date within 7d), reconciles with latest history bar, and window stats (1y/2y/5y/7y/10y) present'
    },
    {
      id: 'macro_bitcoin_coingecko',
      name: 'Bitcoin (CoinGecko)',
      category: 'Macro Dashboard',
      module: 'Stock Analyzer / Research',
      component: 'Bitcoin',
      description: 'Verifies CoinGecko BTC: current and ATH > 0, pct_off_ath numeric, history series non-empty'
    },
    {
      id: 'macro_econ_indicators_fred',
      name: 'Economic Indicators (FRED)',
      category: 'Macro Dashboard',
      module: 'Stock Analyzer / Research',
      component: 'Economic Indicators',
      description: 'Verifies all 15 FRED economic indicators load with a numeric current value and history, and no per-indicator errors'
    },
    {
      id: 'macro_econ_next_release_date',
      name: 'Indicator Next Release Date',
      category: 'Macro Dashboard',
      module: 'Stock Analyzer / Research',
      component: 'Economic Indicators',
      description: 'Verifies a majority of economic indicators carry next_release_date — the field the "sort by next release" toggle orders by'
    },
    {
      id: 'macro_fed_watch',
      name: 'Fed Watch (FOMC)',
      category: 'Macro Dashboard',
      module: 'Stock Analyzer / Research',
      component: 'Fed Watch',
      description: 'Verifies Fed Watch returns upcoming FOMC meetings, each with a meeting_date'
    },
    {
      id: 'macro_fed_markets',
      name: 'Fed Markets (Polymarket)',
      category: 'Macro Dashboard',
      module: 'Stock Analyzer / Research',
      component: 'Fed Watch',
      description: 'Verifies the Polymarket cards: annual_outlook (numeric expected_cuts + bars + history) and rate_end_of_year (bars + history). Transient Polymarket throttles are tolerated'
    },
    {
      id: 'macro_score_block',
      name: 'Composite Score Block',
      category: 'Macro Dashboard',
      module: 'Stock Analyzer / Research',
      component: 'Macro Score',
      description: 'Verifies the composite score: economic_health and market_sentiment values in 0-100 with numeric coverage, default_weights + methodology present, history is a list'
    },
    {
      id: 'macro_auth_required',
      name: 'Auth Required',
      category: 'Macro Dashboard',
      module: 'Stock Analyzer / Research',
      component: 'Macro Dashboard',
      description: 'Verifies an unauthenticated GET of the macro dashboard is rejected with 401/403. The endpoint has no feature gate, so IsAuthenticated is the only thing protecting a 6-upstream aggregate and every upstream API quota'
    },
    {
      id: 'macro_json_safe',
      name: 'JSON Safety (no NaN)',
      category: 'Macro Dashboard',
      module: 'Stock Analyzer / Research',
      component: 'Macro Dashboard',
      description: 'Verifies clean_nan_values() still covers every section: no bare NaN/Infinity literals in the body and no non-finite floats in the parsed payload. A single one blanks the entire dashboard because JSON.parse throws before any card mounts'
    },
    {
      id: 'macro_score_history_series',
      name: 'Score History Backtest',
      category: 'Macro Dashboard',
      module: 'Stock Analyzer / Research',
      component: 'Macro Score',
      description: 'Verifies score.history is a real backtested series and not the empty list a swallowed backtest_scores() failure produces: 24+ monthly points, unique ascending dates, values in 0-100, and ending within ~70 days of today'
    },
    {
      id: 'macro_score_signals',
      name: 'Score Signals & Weights',
      category: 'Macro Dashboard',
      module: 'Stock Analyzer / Research',
      component: 'Macro Score',
      description: 'Verifies per-signal sub-scores are present and every signal key exists in default_weights. A key that drifts out of sync silently gets weight 0 — it vanishes from the headline score and from every user preset with no error'
    },
    {
      id: 'macro_preset_crud',
      name: 'Score Preset CRUD',
      category: 'Macro Dashboard',
      module: 'Stock Analyzer / Research',
      component: 'Macro Score Presets',
      description: 'Round-trips a macro score preset through create → list → update → delete → 404 on /api/stock-analyzer/macro/score-presets/, the persistence layer behind the weights editor'
    },
    {
      id: 'macro_preset_validation',
      name: 'Score Preset Validation',
      category: 'Macro Dashboard',
      module: 'Stock Analyzer / Research',
      component: 'Macro Score Presets',
      description: 'Verifies malformed presets are rejected with 400: unsupported schema_version, non-object weights, no known weight family, negative signal weight, blank name — plus a duplicate name returning a clean 400 rather than a 500 from the unique_together constraint'
    },
    {
      id: 'macro_preset_set_default',
      name: 'Score Preset Set Default',
      category: 'Macro Dashboard',
      module: 'Stock Analyzer / Research',
      component: 'Macro Score Presets',
      description: 'Verifies the default-preset pointer: PATCH set-default returns the id, DELETE clears it to null, and set-default on a deleted preset returns 404. default_macro_score_preset_id is a plain IntegerField, not an FK, so nothing at the DB level prevents a dangling pointer'
    },

    // === Chart Indicators ===
    // Every price chart in the app renders the same component against the same
    // endpoint, so these run once and cover 0DTE, the screener ticker popup,
    // the watchlist card, research and dashboard widgets.
    {
      id: 'chartind_warmup_macd',
      name: 'MACD Covers Window',
      category: 'Charts > Indicator Coverage',
      module: 'Charts',
      component: 'Price Chart',
      description: 'Verifies MACD has a real value on the FIRST bar of the window. Without warm-up history both moving averages seed to the first close, so MACD reads exactly 0.000 — this rejects that'
    },
    {
      id: 'chartind_warmup_rsi',
      name: 'RSI Covers Window',
      category: 'Charts > Indicator Coverage',
      module: 'Charts',
      component: 'Price Chart',
      description: 'Verifies RSI starts at bar one with no leading blanks, and is not pinned at 99.01 — the value a zero-seeded average produces before the first down-day'
    },
    {
      id: 'chartind_warmup_bollinger',
      name: 'Bollinger Covers Window',
      category: 'Charts > Indicator Coverage',
      module: 'Charts',
      component: 'Price Chart',
      description: 'Verifies the bands exist on the first bar and are not pinched shut. A cold start funnels open from near-zero because the standard deviation of one or two samples is a fraction of the real spread'
    },
    {
      id: 'chartind_warmup_all',
      name: 'All Indicators Cover Window',
      category: 'Charts > Indicator Coverage',
      module: 'Charts',
      component: 'Price Chart',
      description: 'Requests MACD, RSI, Bollinger, EMA and SMA-50 together and verifies none of them are blank on the first bar'
    },
    {
      id: 'chartind_warmup_short_history',
      name: 'Short History Is Partial',
      category: 'Charts > Indicator Coverage',
      module: 'Charts',
      component: 'Price Chart',
      description: 'Requests a 720-period average over a 1-year window. Whatever history allows, the answer must be honest: either fully covered, or reported partial with the unconverged head left blank rather than fabricated'
    },
    {
      id: 'chartind_math_rsi_bounds',
      name: 'RSI Within 0-100',
      category: 'Charts > Indicator Math',
      module: 'Charts',
      component: 'Price Chart',
      description: 'RSI is a bounded oscillator; verifies every returned value sits inside 0-100'
    },
    {
      id: 'chartind_math_bollinger_order',
      name: 'Bollinger Band Ordering',
      category: 'Charts > Indicator Math',
      module: 'Charts',
      component: 'Price Chart',
      description: 'Verifies lower <= middle <= upper on every bar'
    },
    {
      id: 'chartind_math_macd_histogram',
      name: 'MACD Histogram Identity',
      category: 'Charts > Indicator Math',
      module: 'Charts',
      component: 'Price Chart',
      description: 'The histogram is MACD minus its signal line by definition; verifies that identity holds on every bar'
    },
    {
      id: 'chartind_math_sma_recompute',
      name: 'SMA Matches Recomputation',
      category: 'Charts > Indicator Math',
      module: 'Charts',
      component: 'Price Chart',
      description: 'Independently recomputes SMA-50 from the returned closing prices and compares it to the server value — catches a broken calculation, not just a missing one'
    },
    {
      id: 'chartind_config_macd_custom',
      name: 'Custom MACD Honored',
      category: 'Charts > Indicator Math',
      module: 'Charts',
      component: 'Price Chart',
      description: 'Verifies a requested MACD 6/13/5 produces a materially different series than the 12/26/9 default — if settings stop reaching the calculation the chart silently shows defaults'
    },
    {
      id: 'chartind_config_rsi_custom',
      name: 'Custom RSI Honored',
      category: 'Charts > Indicator Math',
      module: 'Charts',
      component: 'Price Chart',
      description: 'Verifies a requested 9-period RSI differs from the 14-period default'
    },
    {
      id: 'chartind_config_invalid_json',
      name: 'Invalid Config Rejected',
      category: 'Charts > Indicator Math',
      module: 'Charts',
      component: 'Price Chart',
      description: 'Verifies malformed indicator_config returns 400 rather than being silently ignored'
    },
    {
      id: 'chartind_intraday_1d_session',
      name: '1D Single Session',
      category: 'Charts > Intraday',
      module: 'Charts',
      component: '0DTE Price Chart',
      description: 'Verifies the 1D range returns one regular-hours session of 5-minute bars, with no pre/post-market bars leaking in'
    },
    {
      id: 'chartind_intraday_1w_sessions',
      name: '1W Five Sessions',
      category: 'Charts > Intraday',
      module: 'Charts',
      component: '0DTE Price Chart',
      description: 'Verifies the 1W range returns five trading sessions of 30-minute bars, all within regular hours'
    },
    {
      id: 'chartind_intraday_vwap_reset',
      name: 'Intraday VWAP Resets',
      category: 'Charts > Intraday',
      module: 'Charts',
      component: '0DTE Price Chart',
      description: 'VWAP must restart each session. At the opening bar the running average is that bar alone, so VWAP should equal its own typical price'
    },
    {
      id: 'chartind_intraday_warmup',
      name: 'Intraday Warm From Bar One',
      category: 'Charts > Intraday',
      module: 'Charts',
      component: '0DTE Price Chart',
      description: 'Verifies intraday indicators are converged on the first bar using 0DTE periods (MACD 6/13/5, RSI 9)'
    },
    {
      id: 'chartind_intraday_unsupported',
      name: 'Intraday Refused For Index',
      category: 'Charts > Intraday',
      module: 'Charts',
      component: '0DTE Price Chart',
      description: 'Index tickers have no intraday coverage. Verifies a 1D request is refused with a usable error rather than quietly serving daily bars under an intraday label'
    },
    {
      id: 'chartind_range_validation',
      name: 'Unknown Range Rejected',
      category: 'Charts > Intraday',
      module: 'Charts',
      component: 'Price Chart',
      description: 'Verifies an unsupported range returns 400 listing the valid ranges, instead of silently falling back to a default window'
    },
    {
      id: 'chartind_surface_zerodte',
      name: '0DTE Card',
      category: 'Charts > Surfaces',
      module: 'Charts',
      component: '0DTE Price Chart',
      description: 'Reproduces what the 0DTE card sends on both a daily and an intraday range, and verifies indicators resolve on each'
    },
    {
      id: 'chartind_surface_screener_popup',
      name: 'Screener Ticker Popup',
      category: 'Charts > Surfaces',
      module: 'Charts',
      component: 'Options Premium Chart Modal',
      description: 'Clicking a ticker in the screener opens a modal with two charts. Resolves a really-listed contract and verifies both the underlying (with indicators) and the premium series load'
    },
    {
      id: 'chartind_surface_watchlist_card',
      name: 'Watchlist Contract Card',
      category: 'Charts > Surfaces',
      module: 'Charts',
      component: 'Contract Card Charts',
      description: 'Uses the watchlist card own default ranges (premium 3m, underlying 1y) and verifies indicators cover the underlying window'
    },
    {
      id: 'chartind_surface_research',
      name: 'Research / Stock Analyzer',
      category: 'Charts > Surfaces',
      module: 'Charts',
      component: 'Price Chart Modal',
      description: 'Research and Stock Analyzer share one modal where indicators are opt-in. Verifies no indicator columns appear unless requested, and that they resolve when they are'
    },
    {
      id: 'chartind_surface_dashboard_widget',
      name: 'Dashboard Widget',
      category: 'Charts > Surfaces',
      module: 'Charts',
      component: 'Dashboard Chart Widget',
      description: 'Dashboard widgets always send a full indicator_config object rather than the CSV form; verifies that path resolves every indicator'
    },
    {
      id: 'chartind_options_no_indicators',
      name: 'Options Chart Has None',
      category: 'Charts > Surfaces',
      module: 'Charts',
      component: 'Options Premium Chart',
      description: 'The premium endpoint returns price bars only. Its tooltip shows MACD/RSI, but those are the underlying chart values passed across by the linked crosshair. Verifies no indicator columns ever appear here'
    },

    // === 0DTE > Engine Path ===
    // The /0dte page has no endpoint of its own — it reuses the screener and is
    // distinguished only by exp_from_days/exp_to_days and strike_low_pct/high_pct.
    {
      id: 'zerodte_explicit_window',
      name: 'Explicit Expiration Window Honored',
      category: '0DTE > Engine',
      module: '0DTE',
      component: '0DTE Screener',
      description: 'Verifies every returned expiration falls inside the requested band. The legacy tier logic can only search ±30d around a target, which cannot isolate a "1–3 weeks" band — this is the whole reason the module exists'
    },
    {
      id: 'zerodte_expiration_cap',
      name: 'Expiration Cap Keeps Soonest',
      category: '0DTE > Engine',
      module: '0DTE',
      component: '0DTE Screener',
      description: 'A wide band can contain many weekly expirations. Verifies at most 4 are returned and that they are the soonest listings, not the ones nearest the band midpoint'
    },
    {
      id: 'zerodte_strike_band',
      name: 'Strike Band Returns Chain',
      category: '0DTE > Engine',
      module: '0DTE',
      component: '0DTE Screener',
      description: 'With a strike range supplied the engine keeps every listed strike inside the band per expiration (chain-like) rather than the single closest. Verifies the band engages, respects the 12-strike cap, and contains no strike outside it'
    },
    {
      id: 'zerodte_band_literal_puts',
      name: 'Put Band Applied Literally',
      category: '0DTE > Engine',
      module: '0DTE',
      component: '0DTE Screener',
      description: 'Put sign handling lives entirely in the frontend builder. Sends a put band entirely below spot and verifies the engine never flips it — a flip would put target-price mode on the wrong side of the money'
    },
    {
      id: 'zerodte_forecast_fields',
      name: 'Forecast Descriptors Present',
      category: '0DTE > Engine',
      module: '0DTE',
      component: '0DTE Screener',
      description: 'Verifies _iv_skew (sticky-moneyness smile) and _event (earnings vol-crush) are present and well-shaped on short-dated rows. Both are computed defensively and may be null, so shape is checked only when populated'
    },
    {
      id: 'zerodte_param_whitelist',
      name: 'Intraday Params Survive Whitelist',
      category: '0DTE > Engine',
      module: '0DTE',
      component: '0DTE Screener',
      description: 'run_multiple_analyses drops any param not in its allow-list. If a window key were dropped the run would silently return the wrong contracts rather than error. Proves survival by contrast against the same scenario run without the keys'
    },

    // === 0DTE > Isolation ===
    // Turns the "new modes must not change Basic/Intermediate" rule into a check.
    {
      id: 'zerodte_isolation_basic',
      name: 'Basic Mode Unaffected',
      category: '0DTE > Isolation',
      module: '0DTE',
      component: 'Screener (Basic)',
      description: 'Runs a Basic-shaped scenario and verifies no intraday leakage: forecast descriptors stay null and Strike Position stays the uniform scenario value rather than the per-row moneyness the short-dated branch computes'
    },
    {
      id: 'zerodte_isolation_intermediate',
      name: 'Intermediate Mode Unaffected',
      category: '0DTE > Isolation',
      module: '0DTE',
      component: 'Screener (Intermediate)',
      description: 'Same isolation checks against an Intermediate-shaped scenario'
    },

    // === Portfolio ===
    // The Portfolio is a designated WatchlistGroup (is_portfolio=True) that reuses the
    // watchlist's group, contract and simulate endpoints. Its invariants are therefore
    // single-line guards living inside code the Watchlist also runs — these turn each
    // one into something that fails loudly.
    {
      id: 'portfolio_get_creates_once',
      name: 'GET Creates Group Once',
      category: 'Portfolio > Group',
      module: 'Portfolio',
      component: 'Portfolio Page',
      description: 'The Portfolio page calls GET /portfolio/ on every mount. Verifies get_or_create is idempotent, so repeat visits return the same group instead of accumulating one per visit'
    },
    {
      id: 'portfolio_hidden_from_groups',
      name: 'Hidden From Group List',
      category: 'Portfolio > Group',
      module: 'Portfolio',
      component: 'Watchlist Groups',
      description: 'The portfolio must not render as a duplicate group on the Watchlist page. Verifies GET /watchlist-groups/ filters it out while still returning ordinary groups'
    },
    {
      id: 'portfolio_rename_blocked',
      name: 'Rename Rejected',
      category: 'Portfolio > Group',
      module: 'Portfolio',
      component: 'Watchlist Groups',
      description: 'Renaming the portfolio from the Watchlist UI returns 403, and the name is genuinely unchanged afterwards rather than only reporting an error'
    },
    {
      id: 'portfolio_delete_blocked',
      name: 'Delete Rejected',
      category: 'Portfolio > Group',
      module: 'Portfolio',
      component: 'Watchlist Groups',
      description: 'Deleting the portfolio returns 403 and the group survives, so the Portfolio cannot be dismantled from the Watchlist page'
    },
    {
      id: 'portfolio_flag_not_settable',
      name: 'is_portfolio Read-Only',
      category: 'Portfolio > Group',
      module: 'Portfolio',
      component: 'Watchlist Groups',
      description: 'POSTing is_portfolio=true to the generic group endpoint must be ignored. Without this any user could mint a second portfolio, and "exactly one per user" stops holding'
    },
    {
      id: 'portfolio_stock_holding_equity',
      name: 'Stock Equity Multiplier',
      category: 'Portfolio > Holdings',
      module: 'Portfolio',
      component: 'Add Positions',
      description: 'A share covers itself; an option contract covers 100. Verifies stock equity is shares x price and calls out the x100 regression explicitly, which would silently report 100x the real position value'
    },
    {
      id: 'portfolio_stock_cost_basis',
      name: 'Stock Cost Basis',
      category: 'Portfolio > Holdings',
      module: 'Portfolio',
      component: 'Add Positions',
      description: 'Mirrors the option rule: a supplied average cost becomes the baseline every % change is measured against, and today’s market price is the fallback when none is given'
    },
    {
      id: 'portfolio_stock_nulls_option_fields',
      name: 'Stock Nulls Contract Fields',
      category: 'Portfolio > Holdings',
      module: 'Portfolio',
      component: 'Saved Contracts',
      description: 'A stock holding has no contract identity. Verifies option_type/strike/expiration are nulled even when the client sends them, rather than being trusted'
    },
    {
      id: 'portfolio_option_requires_fields',
      name: 'Option Fields Still Required',
      category: 'Portfolio > Holdings',
      module: 'Portfolio',
      component: 'Saved Contracts',
      description: 'Those columns were made nullable only so stock could share the model. Verifies an option with no strike or expiration is still rejected, so watchlist validation is exactly as strict as before stocks existed'
    },
    {
      id: 'portfolio_stock_update_target',
      name: 'Stock Target Update Equity',
      category: 'Portfolio > Holdings',
      module: 'Portfolio',
      component: 'Edit Holding',
      description: 'Editing a stock’s unit count recalculates equity through share_multiplier. Catches the x100 regression on the update path and checks a unit count of zero is rejected'
    },
    {
      id: 'portfolio_all_stock_simulate',
      name: 'All-Stock Group Returns Empty',
      category: 'Portfolio > Simulation',
      module: 'Portfolio',
      component: 'Run Portfolio',
      description: 'A portfolio holding only stock has nothing to simulate, but the holdings were still re-priced. Verifies this returns an empty list with 200 — a success, not an error'
    },
    {
      id: 'portfolio_mixed_group_simulate',
      name: 'Mixed Group Excludes Stock',
      category: 'Portfolio > Simulation',
      module: 'Portfolio',
      component: 'Run Portfolio',
      description: 'Stock cannot go through the options engine, which requires a strike and expiration and hard-fails without them. Verifies stock is excluded from the results yet still refreshed'
    },
    {
      id: 'portfolio_peers_normalization',
      name: 'Peer Set Normalization',
      category: 'Portfolio > Company Intel',
      module: 'Portfolio',
      component: 'Company Intel',
      description: 'Peer lists are uppercased, deduped, stripped of the anchor ticker and over-long symbols, and capped at 8 — the cap exists because these upstream calls are uncached and sleep-paced per ticker'
    },
    {
      id: 'portfolio_peers_upsert',
      name: 'Peer Set Upsert',
      category: 'Portfolio > Company Intel',
      module: 'Portfolio',
      component: 'Company Intel',
      description: 'PUT is an upsert so the frontend never has to track whether a row exists. Verifies a second write replaces the set rather than appending or creating a duplicate row'
    },
    {
      id: 'portfolio_peers_empty_deletes',
      name: 'Empty Peer List Deletes',
      category: 'Portfolio > Company Intel',
      module: 'Portfolio',
      component: 'Company Intel',
      description: '"No peers" and "no peer set" are the same thing to the UI. Verifies PUT with an empty list removes the row instead of storing an empty array'
    },
    {
      id: 'portfolio_peers_delete',
      name: 'Peer Set Delete',
      category: 'Portfolio > Company Intel',
      module: 'Portfolio',
      component: 'Company Intel',
      description: 'DELETE clears a saved comparison set and it does not reappear in the peer-set map'
    },
    {
      id: 'portfolio_peers_user_isolation',
      name: 'Peer Set User Isolation',
      category: 'Portfolio > Company Intel',
      module: 'Portfolio',
      component: 'Company Intel',
      description: 'Two users holding the same ticker must not see each other’s comparison sets. Seeds a second user on the same anchor ticker so this proves separation rather than mere absence'
    },
    {
      id: 'portfolio_logic_share_multiplier',
      name: 'Share Multiplier',
      category: 'Portfolio > Logic',
      module: 'Portfolio',
      component: 'SavedContract Model',
      description: 'Pure logic, no network: share_multiplier is 1 for stock and 100 for an option, and asset_type still defaults to option so rows saved before the Portfolio existed keep their meaning'
    },
    {
      id: 'portfolio_logic_sell_inversion',
      name: 'Sell-Side Inversion',
      category: 'Portfolio > Logic',
      module: 'Portfolio',
      component: 'SavedContract Model',
      description: 'For a short position a rising premium is a loss, so premium and equity % change invert. Also checks the abs() denominator keeps the sign right for a negative baseline (puts, theta)'
    },
    {
      id: 'portfolio_logic_stock_quote_guards',
      name: 'Stock Quote Guards',
      category: 'Portfolio > Logic',
      module: 'Portfolio',
      component: 'portfolio_analysis',
      description: 'A failed price fetch must return None, never 0 — callers treat None as "leave the stored values alone", so a zero would overwrite a good price with junk. Also covers share and cost-basis coercion'
    },
    {
      id: 'portfolio_logic_serializer_validate',
      name: 'Serializer Validation',
      category: 'Portfolio > Logic',
      module: 'Portfolio',
      component: 'SavedContractSerializer',
      description: 'Validation without HTTP: contract fields nulled for stock, mandatory for an option, read off the instance on a partial update, and empty-string numerics coerced to null'
    },

    // === Dashboard Builder > Technical Indicators ===
    // These backend tests existed but were unreachable from this dashboard.
    {
      id: 'dashboard_macd_calculation',
      name: 'MACD Calculation',
      category: 'Dashboard Builder > Technical Indicators',
      module: 'Dashboard Builder',
      component: 'Technical Indicators',
      description: 'Unit-tests the MACD calculation directly against a synthetic price series'
    },
    {
      id: 'dashboard_rsi_calculation',
      name: 'RSI Calculation',
      category: 'Dashboard Builder > Technical Indicators',
      module: 'Dashboard Builder',
      component: 'Technical Indicators',
      description: 'Unit-tests the RSI calculation directly and verifies values stay within 0-100'
    },
    {
      id: 'dashboard_bollinger_calculation',
      name: 'Bollinger Calculation',
      category: 'Dashboard Builder > Technical Indicators',
      module: 'Dashboard Builder',
      component: 'Technical Indicators',
      description: 'Unit-tests the Bollinger Bands calculation and band ordering'
    },
    {
      id: 'dashboard_indicators_edge_cases',
      name: 'Indicator Edge Cases',
      category: 'Dashboard Builder > Technical Indicators',
      module: 'Dashboard Builder',
      component: 'Technical Indicators',
      description: 'Exercises short series, flat prices and other degenerate inputs to the indicator helpers'
    },
    {
      id: 'dashboard_widget_with_macd',
      name: 'Widget With MACD',
      category: 'Dashboard Builder > Technical Indicators',
      module: 'Dashboard Builder',
      component: 'Chart Widget',
      description: 'Creates a chart widget configured with MACD and verifies it persists'
    },
    {
      id: 'dashboard_widget_with_rsi',
      name: 'Widget With RSI',
      category: 'Dashboard Builder > Technical Indicators',
      module: 'Dashboard Builder',
      component: 'Chart Widget',
      description: 'Creates a chart widget configured with RSI and verifies it persists'
    },
    {
      id: 'dashboard_widget_with_bollinger',
      name: 'Widget With Bollinger',
      category: 'Dashboard Builder > Technical Indicators',
      module: 'Dashboard Builder',
      component: 'Chart Widget',
      description: 'Creates a chart widget configured with Bollinger Bands and verifies it persists'
    },
    {
      id: 'dashboard_widget_all_indicators',
      name: 'Widget All Indicators',
      category: 'Dashboard Builder > Technical Indicators',
      module: 'Dashboard Builder',
      component: 'Chart Widget',
      description: 'Creates a chart widget with every indicator enabled at once'
    },
    {
      id: 'dashboard_chart_data_macd',
      name: 'Chart API MACD',
      category: 'Dashboard Builder > Technical Indicators',
      module: 'Dashboard Builder',
      component: 'Chart Data API',
      description: 'Fetches chart data with MACD requested and verifies the MACD fields are present'
    },
    {
      id: 'dashboard_chart_data_rsi',
      name: 'Chart API RSI',
      category: 'Dashboard Builder > Technical Indicators',
      module: 'Dashboard Builder',
      component: 'Chart Data API',
      description: 'Fetches chart data with RSI requested and verifies the RSI field is present'
    },
    {
      id: 'dashboard_chart_data_bollinger',
      name: 'Chart API Bollinger',
      category: 'Dashboard Builder > Technical Indicators',
      module: 'Dashboard Builder',
      component: 'Chart Data API',
      description: 'Fetches chart data with Bollinger Bands requested and verifies the band fields are present'
    },
    {
      id: 'dashboard_chart_data_all',
      name: 'Chart API All Indicators',
      category: 'Dashboard Builder > Technical Indicators',
      module: 'Dashboard Builder',
      component: 'Chart Data API',
      description: 'Fetches chart data with every indicator requested at once'
    },
    {
      id: 'dashboard_invalid_ticker',
      name: 'Invalid Ticker Handling',
      category: 'Dashboard Builder > Technical Indicators',
      module: 'Dashboard Builder',
      component: 'Chart Data API',
      description: 'Verifies an unknown ticker is handled gracefully rather than raising'
    },
    {
      id: 'dashboard_insufficient_data',
      name: 'Insufficient Data',
      category: 'Dashboard Builder > Technical Indicators',
      module: 'Dashboard Builder',
      component: 'Chart Data API',
      description: 'Verifies indicators degrade gracefully when there are too few bars to compute them'
    },
    {
      id: 'dashboard_nan_handling',
      name: 'NaN Value Handling',
      category: 'Dashboard Builder > Technical Indicators',
      module: 'Dashboard Builder',
      component: 'Chart Data API',
      description: 'Verifies NaN indicator values serialize as null rather than breaking the JSON response'
    },
    {
      id: 'dashboard_inf_handling',
      name: 'Infinity Value Handling',
      category: 'Dashboard Builder > Technical Indicators',
      module: 'Dashboard Builder',
      component: 'Chart Data API',
      description: 'Verifies infinite indicator values serialize as null rather than breaking the JSON response'
    },
  ];

  const handleRunTests = async () => {
    setError('');

    // Production runs need an explicit selection of at most 60 tests. The
    // backend refuses anything else with a 400; this says so before the click.
    if (isProductionServer && selectedTests.length === 0) {
      setError('Select the tests to run; running everything against production is blocked.');
      return;
    }
    if (isProductionServer && selectedTests.length > PRODUCTION_MAX_TESTS_PER_RUN) {
      setError(
        `Select at most ${PRODUCTION_MAX_TESTS_PER_RUN} tests per production run ` +
        `(${selectedTests.length} selected). Run one category at a time.`
      );
      return;
    }

    setIsRunning(true);
    setShowProgressModal(true);

    try {
      const testsToRun = selectedTests.length > 0 ? selectedTests : testTypes.map(t => t.id);
      const result = await runAdminTests(environment, testsToRun);

      if (result.error) {
        setError(result.error);
        setShowProgressModal(false);
        setIsRunning(false);
      } else {
        // Store test run ID for progress modal
        setCurrentTestRunId(result.id);
        setCurrentRun(result);
        // Progress modal will handle polling
      }
    } catch (err) {
      // run/ is only mounted when the backend has ADMIN_TESTS_RUNNER_ENABLED=true
      if (err.response?.status === 404) {
        setError('The test runner is off on this server (ADMIN_TESTS_RUNNER_ENABLED).');
      } else {
        // 503 runner_misconfigured, 409 run_in_progress, 400 selection limits
        setError(describeRunError(err));
      }
      setShowProgressModal(false);
      setIsRunning(false);
      // The flag or the test account may have changed since the page loaded
      loadServerInfo();
    }
  };

  const handleProgressComplete = async (finalStatus) => {
    setShowProgressModal(false);
    setIsRunning(false);

    // Fetch the full test run details including individual test results
    if (finalStatus && finalStatus.id) {
      try {
        const fullDetails = await getTestRunDetail(finalStatus.id);
        setCurrentRun(fullDetails);
      } catch (error) {
        console.error('Failed to fetch test run details:', error);
        setCurrentRun(finalStatus); // Fall back to status data
      }
    } else {
      setCurrentRun(finalStatus);
    }

    setActiveTab('current'); // Switch to results tab
    loadTestHistory();
  };

  const handleCloseProgressModal = () => {
    setShowProgressModal(false);
    setIsRunning(false);
    // Tests continue running in background
  };

  const pollTestStatus = async (runId) => {
    const pollInterval = setInterval(async () => {
      try {
        const details = await getTestRunDetail(runId);
        setCurrentRun(details);

        if (details.status !== 'running') {
          clearInterval(pollInterval);
          loadTestHistory();
        }
      } catch (err) {
        clearInterval(pollInterval);
        console.error('Failed to poll test status:', err);
      }
    }, 2000); // Poll every 2 seconds
  };

  const toggleTestSelection = (testId) => {
    setSelectedTests(prev =>
      prev.includes(testId)
        ? prev.filter(id => id !== testId)
        : [...prev, testId]
    );
  };

  const selectAllTests = () => {
    setSelectedTests(testTypes.map(t => t.id));
  };

  const deselectAllTests = () => {
    setSelectedTests([]);
  };

  const selectCategory = (category) => {
    const categoryTests = testTypes
      .filter(t => t.category === category)
      .map(t => t.id);
    setSelectedTests(categoryTests);
  };

  return (
    <div className="admin-test-dashboard">
      <div className="dashboard-header" style={{ position: 'relative' }}>
        <h1>Admin Testing Dashboard</h1>
        <p className="dashboard-subtitle">Run unit tests to verify system functionality</p>
      </div>

      <div className="dashboard-tabs">
        <button
          className={`tab-btn ${activeTab === 'run' ? 'active' : ''}`}
          onClick={() => setActiveTab('run')}
        >
          Run Tests
        </button>
        <button
          className={`tab-btn ${activeTab === 'current' ? 'active' : ''}`}
          onClick={() => setActiveTab('current')}
          disabled={!currentRun}
        >
          Current Results
        </button>
        <button
          className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          Test History
        </button>
        <button
          className={`tab-btn ${activeTab === 'data' ? 'active' : ''}`}
          onClick={() => setActiveTab('data')}
        >
          Data Loading
        </button>
        <button
          className={`tab-btn ${activeTab === 'roles' ? 'active' : ''}`}
          onClick={() => setActiveTab('roles')}
        >
          Roles
        </button>
        <button
          className={`tab-btn ${activeTab === 'reports' ? 'active' : ''}`}
          onClick={() => setActiveTab('reports')}
        >
          Reports
        </button>
        <button
          className={`tab-btn ${activeTab === 'deletions' ? 'active' : ''}`}
          onClick={() => setActiveTab('deletions')}
        >
          Deletions
        </button>
        <button
          className={`tab-btn ${activeTab === 'cleanup' ? 'active' : ''}`}
          onClick={() => setActiveTab('cleanup')}
        >
          Cleanup
        </button>
        <button
          className={`tab-btn ${activeTab === 'videos' ? 'active' : ''}`}
          onClick={() => setActiveTab('videos')}
        >
          Videos
        </button>
        <button
          className={`tab-btn ${activeTab === 'payouts' ? 'active' : ''}`}
          onClick={() => setActiveTab('payouts')}
        >
          Payouts
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="tab-content">
        {activeTab === 'run' && (
          <TestRunner
            environment={environment}
            setEnvironment={setEnvironment}
            testTypes={testTypes}
            selectedTests={selectedTests}
            toggleTestSelection={toggleTestSelection}
            selectAllTests={selectAllTests}
            deselectAllTests={deselectAllTests}
            selectCategory={selectCategory}
            handleRunTests={handleRunTests}
            isRunning={isRunning}
            serverInfo={serverInfo}
            maxTestsPerRun={isProductionServer ? PRODUCTION_MAX_TESTS_PER_RUN : null}
          />
        )}

        {activeTab === 'current' && currentRun && (
          <TestResults testRun={currentRun} />
        )}

        {activeTab === 'history' && (
          <TestHistory
            testHistory={testHistory}
            onSelectRun={async (run) => {
              // Fetch full details including individual test results
              if (run && run.id) {
                try {
                  const fullDetails = await getTestRunDetail(run.id);
                  setCurrentRun(fullDetails);
                } catch (error) {
                  console.error('Failed to fetch test run details:', error);
                  setCurrentRun(run); // Fall back to summary data
                }
              } else {
                setCurrentRun(run);
              }
              setActiveTab('current');
            }}
          />
        )}

        {activeTab === 'data' && (
          <DataLoading />
        )}

        {activeTab === 'roles' && (
          <RolesManagement />
        )}

        {activeTab === 'reports' && (
          <Reports />
        )}

        {activeTab === 'deletions' && (
          <PendingDeletions />
        )}

        {activeTab === 'cleanup' && (
          <TestUserCleanup />
        )}

        {activeTab === 'videos' && (
          <VideoManager />
        )}

        {activeTab === 'payouts' && (
          <AffiliatePayouts />
        )}
      </div>

      {/* Progress Modal */}
      {showProgressModal && currentTestRunId && (
        <TestProgressModal
          testRunId={currentTestRunId}
          onComplete={handleProgressComplete}
          onClose={handleCloseProgressModal}
        />
      )}
    </div>
  );
}

export default AdminTestDashboard;